import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import CollegeJourneyNavigation from './CollegeJourneyNavigation';
import CollegeNavigationBar from './CollegeNavigationBar';
import timelineService from '../../services/timelineService';
import { parseDeepLinkParams, getSectionExpansionState } from '../../utils/deepLinkUtils';
import RequirementsFetchOrchestrator from '../../services/RequirementsFetchOrchestrator';
import { ApplicationRequirementsService } from '../../lib/application-requirements-service';
import RequirementsFetchLoader from '../CollegeJourney/RequirementsFetchLoader';
import {
  ChevronDown,
  ChevronRight,
  Calendar,
  FileText,
  Target,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Check,
  Clock,
  ExternalLink,
  Star,
  Lightbulb,
  BarChart3,
  Sparkles,
  RefreshCw,
  Plus,
  MapPin,
  GraduationCap,
  Heart,
  DollarSign,
  Building,
  Brain,
  Award,
  Circle,
  CheckCircle2,
  PauseCircle,
  Edit3,
  Trash2,
  X,
  Save
} from 'lucide-react';

// Task status constants
const TASK_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  BLOCKED: 'blocked',
  COMPLETED: 'completed'
};

// Helper functions for application round format conversion
const APPLICATION_ROUND_LABELS = {
  early_decision: 'Early Decision',
  early_action: 'Early Action',
  early_decision_2: 'Early Decision II',
  regular: 'Regular Decision',
  rolling: 'Rolling Admission'
};

const APPLICATION_ROUND_VALUES = {
  'Early Decision': 'early_decision',
  'Early Action': 'early_action',
  'Early Decision II': 'early_decision_2',
  'Regular Decision': 'regular',
  'Rolling Admission': 'rolling'
};

const getApplicationRoundLabel = (value) => {
  return APPLICATION_ROUND_LABELS[value] || 'Regular Decision';
};

const getApplicationRoundValue = (label) => {
  return APPLICATION_ROUND_VALUES[label] || 'regular';
};

// Helper function to parse date strings in local timezone (not UTC)
// Fixes timezone issues where dates shift backward by one day
const parseLocalDate = (dateString) => {
  if (!dateString) return null;

  // If it's already a Date object, return it
  if (dateString instanceof Date) return dateString;

  // Parse YYYY-MM-DD format in local timezone
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

const CollegeStrategyV2 = () => {
  const { collegeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    colleges,
    loadCollegeJourneyData,
    updateStudentDeadline,
    updateStudentEssayPrompts,
    updateStudentRequirements,
    getVerificationStatus
  } = useCollegeContext();
  const { currentUser } = useFamilyContext();

  // ============ STATE MANAGEMENT ============
  const [activeTab, setActiveTab] = useState('strategy');
  const [expandedSections, setExpandedSections] = useState({
    detailedStats: false,
    valueProposition: false,
    strategicResearch: false,
    completeBreakdown: false,
    immediateActions: true,  // Expand by default
    primaryDevelopment: true,  // Expand by default
    positioning: true,  // Expand by default
    essayPrompts: true  // Expand by default so Edit button is visible
  });
  const [college, setCollege] = useState(null);
  const [globalCollegeData, setGlobalCollegeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApplicationRound, setSelectedApplicationRound] = useState('regular');
  const [applicationStatus, setApplicationStatus] = useState('planning'); // planning, in_progress, submitted, completed, decision_received
  const [applicationComponents, setApplicationComponents] = useState([]);
  const [databaseApplicationComponents, setDatabaseApplicationComponents] = useState([]);
  const [loadingDatabaseComponents, setLoadingDatabaseComponents] = useState(false);
  const [timelineHealth, setTimelineHealth] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const [valueProposition, setValueProposition] = useState({
    excitement: '',
    growth: '',
    unique: ''
  });
  const [generatingValueProp, setGeneratingValueProp] = useState(false);
  const [aiValuePropSuggestions, setAIValuePropSuggestions] = useState(null);
  const [strategicResearch, setStrategicResearch] = useState({
    programs: '',
    campus: '',
    culture: ''
  });
  const [generatingResearch, setGeneratingResearch] = useState(false);
  const [aiResearchSuggestions, setAIResearchSuggestions] = useState(null);
  const [generatingImprovementPlan, setGeneratingImprovementPlan] = useState(false);

  // ============ AUTO-FETCH STATE ============
  const [showFetchLoader, setShowFetchLoader] = useState(false);
  const [fetchCollegeData, setFetchCollegeData] = useState(null); // Store college data for fetch
  const [studentGraduationYear, setStudentGraduationYear] = useState(null); // Store for requirements fetch

  // ============ STUDENT VERIFICATION STATE ============
  const [editingDeadline, setEditingDeadline] = useState(null); // Which deadline is being edited (e.g., 'ed_deadline')
  const [editingDeadlineValue, setEditingDeadlineValue] = useState('');
  const [verificationStatus, setVerificationStatus] = useState({
    deadlines: false,
    essays: false,
    requirements: false
  });
  const [essaySlots, setEssaySlots] = useState([]); // Student's essay slots
  const [loadingEssaySlots, setLoadingEssaySlots] = useState(false);
  const [editingEssays, setEditingEssays] = useState(false); // Whether essay editing mode is active
  const [editedEssays, setEditedEssays] = useState([]); // Working copy of essays being edited
  const [editingRequirements, setEditingRequirements] = useState(false); // Whether requirements editing mode is active
  const [editedRequirements, setEditedRequirements] = useState([]); // Working copy of requirements - array of {name, details, is_optional}
  const [applicationNotes, setApplicationNotes] = useState(''); // Special notes about application process
  const [editingApplicationNotes, setEditingApplicationNotes] = useState(false);
  const [editedApplicationNotes, setEditedApplicationNotes] = useState('');
  const [oneOffTasks, setOneOffTasks] = useState([]); // Special one-off tasks (like Rice Box)
  const [editingOneOffTasks, setEditingOneOffTasks] = useState(false);
  const [editedOneOffTasks, setEditedOneOffTasks] = useState([]);

  // ============ DEEP LINKING ============
  // Handle deep linking from timeline items or external links
  useEffect(() => {
    const deepLinkParams = parseDeepLinkParams(searchParams);

    // Set active tab if specified in URL
    if (deepLinkParams.tab) {
      setActiveTab(deepLinkParams.tab);
    }

    // Expand section if specified in URL
    if (deepLinkParams.section && deepLinkParams.expand) {
      const sectionState = getSectionExpansionState(deepLinkParams.section);
      setExpandedSections(prev => ({
        ...prev,
        ...sectionState
      }));

      // Scroll to section after a short delay to allow rendering
      if (deepLinkParams.section) {
        setTimeout(() => {
          const sectionElement = document.getElementById(`section-${deepLinkParams.section}`);
          if (sectionElement) {
            sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    }
  }, [searchParams]);

  // ============ DATA FETCHING ============
  useEffect(() => {
    const fetchCollegeData = async () => {
      if (!collegeId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch college_journey_colleges data
        const { data: collegeData, error: collegeError } = await supabase
          .from('college_journey_colleges')
          .select('*')
          .eq('id', collegeId)
          .single();

        if (collegeError) throw collegeError;

        console.log('[CollegeStrategyV2] Loaded college data:', {
          id: collegeData.id,
          name: collegeData.name,
          college_name: collegeData.college_name,
          hasName: !!collegeData.name,
          hasCollegeName: !!collegeData.college_name
        });

        setCollege(collegeData);
        setFetchCollegeData(collegeData); // Store for fetch loader

        // Fetch student graduation year for requirements fetch
        const { data: profileData, error: profileError } = await supabase
          .from('student_academic_profiles')
          .select('graduation_year')
          .eq('user_id', currentUser.id)
          .single();

        if (!profileError && profileData?.graduation_year) {
          setStudentGraduationYear(profileData.graduation_year);
          console.log('[CollegeStrategyV2] Student graduation year:', profileData.graduation_year);
        } else {
          // Fallback to next year if profile not found
          const fallbackYear = new Date().getFullYear() + 1;
          setStudentGraduationYear(fallbackYear);
          console.log('[CollegeStrategyV2] Using fallback graduation year:', fallbackYear);
        }

        // Fetch global_colleges data if available
        if (collegeData?.global_college_id) {
          const { data: globalData, error: globalError } = await supabase
            .from('global_colleges')
            .select('*')
            .eq('id', collegeData.global_college_id)
            .single();

          if (!globalError && globalData) {
            setGlobalCollegeData(globalData);
          }
        }

        // Set default application round (ensure it's in snake_case format)
        if (collegeData?.application_round) {
          setSelectedApplicationRound(collegeData.application_round);
        }

        // Set application status from database
        if (collegeData?.application_status) {
          setApplicationStatus(collegeData.application_status);
        }

        // Check if we need to auto-fetch requirements
        await checkAndTriggerRequirementsFetch(collegeData);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching college data:', error);
        toast.error('Failed to load college data');
        setLoading(false);
      }
    };

    fetchCollegeData();
  }, [collegeId]);

  // ============ AUTO-FETCH REQUIREMENTS ============
  const checkAndTriggerRequirementsFetch = async (collegeData) => {
    if (!collegeData) return;

    try {
      console.log('[CollegeStrategyV2] Checking if requirements fetch is needed for', collegeData.name);
      console.log('[CollegeStrategyV2] Current fetch status:', collegeData.requirements_fetch_status);

      // ONLY trigger fetch modal if:
      // 1. Never fetched before (status = 'not_started')
      // 2. Previous fetch failed (status = 'failed')
      const shouldTriggerFetch =
        collegeData.requirements_fetch_status === 'not_started' ||
        collegeData.requirements_fetch_status === 'failed';

      if (!shouldTriggerFetch) {
        console.log('[CollegeStrategyV2] Requirements already fetched (status: completed), skipping modal');
        return;
      }

      console.log('[CollegeStrategyV2] Fetch status indicates need for fetch:', collegeData.requirements_fetch_status);
      setFetchCollegeData(collegeData);
      setShowFetchLoader(true);
    } catch (error) {
      console.error('[CollegeStrategyV2] Error checking fetch status:', error);
      // Don't trigger fetch on error - let user manually trigger if needed
    }
  };

  const handleFetchComplete = (result) => {
    console.log('[CollegeStrategyV2] Requirements fetch completed:', result);
    setShowFetchLoader(false);
    toast.success('Requirements data fetched! Check the Application tab for details.');
    // Refresh college data to show updated info
    if (currentUser?.id) {
      loadCollegeJourneyData(currentUser.id);
    }
  };

  const handleFetchCancel = () => {
    console.log('[CollegeStrategyV2] User returned to hub during fetch');
    setShowFetchLoader(false);
    navigate('/college');
  };

  // ============ COMPUTED DATA ============
  const mergedCollegeData = useMemo(() => {
    if (!college) return null;

    return {
      // Global college data (institutional) - spread first
      ...(globalCollegeData || {}),
      // College Journey data (user-specific) - spread second to override with user data
      ...college,
      // Prefer college_journey name over global name
      name: college.name || globalCollegeData?.name,
      location: college.location || globalCollegeData?.location
    };
  }, [college, globalCollegeData]);

  // Load database-driven application components
  useEffect(() => {
    const loadDatabaseComponents = async () => {
      if (!globalCollegeData?.id) return;

      // Skip if already loaded for this college
      if (databaseApplicationComponents.length > 0) return;

      setLoadingDatabaseComponents(true);
      try {
        const components = await ApplicationRequirementsService.getFormattedApplicationComponents(globalCollegeData.id);
        setDatabaseApplicationComponents(components);
      } catch (error) {
        console.error('Error loading database application components:', error);
      } finally {
        setLoadingDatabaseComponents(false);
      }
    };

    loadDatabaseComponents();
  }, [globalCollegeData?.id, databaseApplicationComponents.length]);

  // Compute application component list with status
  const applicationComponentsList = useMemo(() => {
    if (!mergedCollegeData) return [];

    // Use database components if available, otherwise fall back to hardcoded list
    const sourceComponents = databaseApplicationComponents.length > 0
      ? databaseApplicationComponents
      : null;

    if (!sourceComponents) {
      // Fall back to hardcoded list
      const defaultComponents = [
      {
        name: 'Common Application',
        details: 'Personal info, activities, honors'
      },
      {
        name: `Application Fee ($${globalCollegeData?.application_fee || 75}) or Fee Waiver`,
        details: 'Check financial aid office for fee waiver eligibility'
      },
      {
        name: 'Official High School Transcript',
        details: 'Request from guidance counselor'
      },
      {
        name: 'Common App Essay (650 words)',
        urgent: true,
        details: 'Personal statement shared across all Common App schools',
        deadline: (selectedApplicationRound?.includes('early') && selectedApplicationRound !== 'early_decision_2') ? 'Nov 1 for ED/EA' : 'Jan 2 for RD'
      },
      {
        name: `${mergedCollegeData.name} Supplemental Essays`,
        urgent: true,
        details: 'College-specific supplemental essays',
        deadline: (selectedApplicationRound?.includes('early') && selectedApplicationRound !== 'early_decision_2') ? 'Nov 1 for ED/EA' : 'Jan 2 for RD'
      },
      {
        name: `Teacher Recommendations (${globalCollegeData?.num_teacher_recs_required || 2})`,
        urgent: true,
        details: 'Request 4-6 weeks before deadline',
        deadline: 'Oct 20 (request deadline)'
      },
      {
        name: 'Counselor Recommendation',
        details: 'Submitted with transcript',
        deadline: (selectedApplicationRound?.includes('early') && selectedApplicationRound !== 'early_decision_2') ? 'Nov 1' : 'Jan 2'
      },
      {
        name: 'SAT/ACT Scores (Optional)',
        details: globalCollegeData?.test_optional ? 'Test optional - submit if strengthens application' : 'Test required',
        deadline: (selectedApplicationRound?.includes('early') && selectedApplicationRound !== 'early_decision_2') ? 'Nov 1' : 'Jan 2'
      },
      {
        name: 'Mid-Year Report',
        details: 'Submitted after Fall semester grades',
        deadline: 'Feb 15'
      }
    ];

      // Merge hardcoded components with loaded timeline items
      return defaultComponents.map(comp => {
        const timelineItem = applicationComponents.find(t => t.title === comp.name);
        return {
          ...comp,
          id: timelineItem?.id,
          status: timelineItem?.status || TASK_STATUS.NOT_STARTED,
          timelineItemExists: !!timelineItem
        };
      });
    }

    // Use database-driven components
    return sourceComponents.map(comp => {
      const timelineItem = applicationComponents.find(t => t.title === comp.name || t.title.includes(comp.name));
      return {
        ...comp,
        id: timelineItem?.id,
        status: timelineItem?.status || TASK_STATUS.NOT_STARTED,
        timelineItemExists: !!timelineItem
      };
    });
  }, [mergedCollegeData, globalCollegeData, selectedApplicationRound, applicationComponents, databaseApplicationComponents]);

  // Calculate completion percentage
  const completionPercentage = useMemo(() => {
    if (applicationComponentsList.length === 0) return 0;
    const completed = applicationComponentsList.filter(c => c.status === TASK_STATUS.COMPLETED).length;
    return Math.round((completed / applicationComponentsList.length) * 100);
  }, [applicationComponentsList]);

  // Calculate Quick Context (fit strength & admission likelihood)
  // Calculate essay and teacher rec counts from required_components
  const applicationCounts = useMemo(() => {
    if (!globalCollegeData) return { numEssays: 0, numTeacherRecs: 0 };

    const required = globalCollegeData.required_components || [];
    const optional = globalCollegeData.optional_components || [];
    const allComponents = [...required, ...optional];

    // Count supplemental essays
    const numEssays = allComponents.filter(c =>
      c.type === 'supplemental_essays' || c.template_key === 'app_supplemental_essays'
    ).length;

    // Count teacher recommendations (each teacher_rec component is one rec)
    const numTeacherRecs = allComponents.filter(c =>
      c.type === 'teacher_rec' || c.template_key?.includes('teacher_rec')
    ).length;

    return { numEssays, numTeacherRecs };
  }, [globalCollegeData?.required_components, globalCollegeData?.optional_components]);

  const quickContext = useMemo(() => {
    if (!mergedCollegeData || !college) return null;

    // Calculate fit strength from AI insights and category
    let fitStrength = 'Unknown';

    if (mergedCollegeData.ai_insights) {
      try {
        const insights = typeof mergedCollegeData.ai_insights === 'string'
          ? JSON.parse(mergedCollegeData.ai_insights)
          : mergedCollegeData.ai_insights;

        // If we have detailed insights, derive fit strength
        if (insights.valuesMatch && insights.academicFit) {
          fitStrength = 'Strong Match';
        } else if (insights.valuesMatch || insights.academicFit) {
          fitStrength = 'Good Match';
        } else {
          fitStrength = 'Potential Match';
        }
      } catch (e) {
        // Fallback to category-based assessment
      }
    }

    // If no insights, use category
    if (fitStrength === 'Unknown') {
      const category = college.category?.toLowerCase();
      if (category === 'target' || category === 'match') {
        fitStrength = 'Strong Match';
      } else if (category === 'safety' || category === 'likely') {
        fitStrength = 'Very Strong Match';
      } else if (category === 'reach') {
        fitStrength = 'Competitive Match';
      }
    }

    // Calculate admission likelihood
    let admissionLikelihood = 'Unknown';
    const acceptanceRate = globalCollegeData?.acceptance_rate;
    const category = college.category?.toLowerCase();

    if (category === 'safety' || category === 'likely') {
      admissionLikelihood = 'Strong Chance';
    } else if (category === 'target' || category === 'match') {
      admissionLikelihood = 'Competitive';
    } else if (category === 'reach') {
      if (acceptanceRate && acceptanceRate < 0.10) {
        admissionLikelihood = 'Highly Competitive';
      } else {
        admissionLikelihood = 'Competitive Reach';
      }
    }

    return {
      fitStrength,
      admissionLikelihood,
      applicationRound: selectedApplicationRound
    };
  }, [mergedCollegeData, college, globalCollegeData, selectedApplicationRound]);

  // Calculate Critical Deadlines based on application round
  const criticalDeadlines = useMemo(() => {
    if (!globalCollegeData && !college) return [];

    const deadlines = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Determine the main deadline based on application round
    let mainDeadline = null;
    let deadlineLabel = '';

    if (selectedApplicationRound === 'early_decision' && globalCollegeData?.ed_deadline) {
      mainDeadline = parseLocalDate(globalCollegeData.ed_deadline);
      deadlineLabel = 'EARLY DECISION DEADLINE';
    } else if (selectedApplicationRound === 'early_action' && globalCollegeData?.ea_deadline) {
      mainDeadline = parseLocalDate(globalCollegeData.ea_deadline);
      deadlineLabel = 'EARLY ACTION DEADLINE';
    } else if (selectedApplicationRound === 'early_decision_2' && globalCollegeData?.ed2_deadline) {
      mainDeadline = parseLocalDate(globalCollegeData.ed2_deadline);
      deadlineLabel = 'EARLY DECISION II DEADLINE';
    } else if (globalCollegeData?.rd_deadline) {
      mainDeadline = parseLocalDate(globalCollegeData.rd_deadline);
      deadlineLabel = 'REGULAR DECISION DEADLINE';
    }

    if (mainDeadline) {
      const daysUntil = Math.ceil((mainDeadline - today) / (1000 * 60 * 60 * 24));
      deadlines.push({
        date: mainDeadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        daysUntil,
        label: deadlineLabel,
        priority: 'high',
        isPast: daysUntil < 0
      });

      // Add recommended preparation deadlines
      // Essay draft: 2 weeks before deadline
      const essayDraftDate = new Date(mainDeadline);
      essayDraftDate.setDate(essayDraftDate.getDate() - 14);
      const essayDaysUntil = Math.ceil((essayDraftDate - today) / (1000 * 60 * 60 * 24));
      if (essayDaysUntil > 0) {
        deadlines.push({
          date: essayDraftDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          daysUntil: essayDaysUntil,
          label: 'Essay draft due',
          priority: 'medium',
          isPast: false
        });
      }

      // Recommendations request: 6 weeks before deadline
      const recDate = new Date(mainDeadline);
      recDate.setDate(recDate.getDate() - 42);
      const recDaysUntil = Math.ceil((recDate - today) / (1000 * 60 * 60 * 24));
      if (recDaysUntil > 0) {
        deadlines.push({
          date: recDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          daysUntil: recDaysUntil,
          label: 'Request recommendations',
          priority: 'medium',
          isPast: false
        });
      }
    }

    return deadlines;
  }, [globalCollegeData, college, selectedApplicationRound]);

  // ============ UTILITY FUNCTIONS ============
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getCategoryBadgeColor = (category) => {
    const normalizedCategory = category?.toLowerCase();
    switch (normalizedCategory) {
      case 'reach':
        return 'bg-coral-400 text-white';
      case 'target':
      case 'match':
        return 'bg-gold-400 text-white';
      case 'safety':
      case 'likely':
        return 'bg-teal-400 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };

  const getCategoryLabel = (category) => {
    const normalizedCategory = category?.toLowerCase();
    if (normalizedCategory === 'reach') return 'REACH';
    if (normalizedCategory === 'target' || normalizedCategory === 'match') return 'TARGET';
    if (normalizedCategory === 'safety' || normalizedCategory === 'likely') return 'SAFETY';
    return category?.toUpperCase() || 'OTHER';
  };

  // ============ EVENT HANDLERS ============
  const handleStatusChange = async (newStatus, isAutoDetected = false) => {
    const previousStatus = applicationStatus;

    // Optimistic update
    setApplicationStatus(newStatus);

    try {
      const updates = {
        application_status: newStatus
      };

      // If marking as submitted, also set submitted date
      if (newStatus === 'submitted' && previousStatus !== 'submitted') {
        updates.application_submitted_date = new Date().toISOString().split('T')[0];
      }

      const { error } = await supabase
        .from('college_journey_colleges')
        .update(updates)
        .eq('id', collegeId);

      if (error) throw error;

      // Only show toast for manual changes, not auto-detected
      if (!isAutoDetected) {
        const statusLabels = {
          planning: 'Planning',
          in_progress: 'In Progress',
          submitted: 'Submitted',
          completed: 'Completed',
          decision_received: 'Decision Received'
        };
        toast.success(`Application status updated to ${statusLabels[newStatus]}`);
      }

      // Refresh college data
      const { data: updatedCollege, error: refreshError } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('id', collegeId)
        .single();

      if (!refreshError && updatedCollege) {
        setCollege(updatedCollege);
      }
    } catch (error) {
      console.error('Error updating application status:', error);
      toast.error('Failed to update application status');
      // Revert optimistic update
      setApplicationStatus(previousStatus);
    }
  };

  const handleApplicationRoundChange = async (round) => {
    setSelectedApplicationRound(round);

    try {
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ application_round: round })
        .eq('id', collegeId);

      if (error) throw error;

      toast.success(`Application round updated to ${getApplicationRoundLabel(round)}`);

      // Refresh college data
      const { data: updatedCollege, error: refreshError } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('id', collegeId)
        .single();

      if (!refreshError && updatedCollege) {
        setCollege(updatedCollege);
      }
    } catch (error) {
      console.error('Error updating application round:', error);
      toast.error('Failed to update application round');
    }
  };

  const handleGenerateInsights = async () => {
    toast.loading('Generating AI insights...');

    try {
      const response = await fetch('/api/anthropic/strategic-features', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-insights',
          userId: currentUser.id,
          collegeId: collegeId,
          collegeName: mergedCollegeData.name
        })
      });

      if (!response.ok) throw new Error('Failed to generate insights');

      const data = await response.json();

      // Update local state
      setCollege(prev => ({ ...prev, ai_insights: data.insights }));

      toast.dismiss();
      toast.success('AI insights generated!');

      // Refresh college data
      const { data: updatedCollege, error: refreshError } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('id', collegeId)
        .single();

      if (!refreshError && updatedCollege) {
        setCollege(updatedCollege);
      }
    } catch (error) {
      console.error('Error generating insights:', error);
      toast.dismiss();
      toast.error('Failed to generate insights');
    }
  };

  // ============ APPLICATION COMPONENTS TRACKING ============

  // Load application components from timeline_items
  useEffect(() => {
    const loadApplicationComponents = async () => {
      if (!currentUser?.id || !collegeId) return;

      try {
        const { data, error } = await supabase
          .from('timeline_items')
          .select('*')
          .eq('user_id', currentUser.id)
          .eq('college_id', collegeId)
          .eq('source_type', 'application_component')
          .order('created_at', { ascending: true });

        if (error) throw error;

        setApplicationComponents(data || []);
      } catch (error) {
        console.error('Error loading application components:', error);
      }
    };

    loadApplicationComponents();
  }, [currentUser?.id, collegeId]);

  // Load timeline health metrics
  useEffect(() => {
    const loadTimelineHealth = async () => {
      if (!currentUser?.id || !collegeId) {
        setLoadingTimeline(false);
        return;
      }

      try {
        // Fetch timeline items for this college
        const { data: items, error } = await supabase
          .from('timeline_items')
          .select('*')
          .eq('user_id', currentUser.id)
          .eq('college_id', collegeId)
          .order('due_date', { ascending: true });

        if (error) throw error;

        // Calculate health metrics
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const overdue = items.filter(item => {
          if (item.completed || item.completion_status === 'completed') return false;
          if (!item.due_date) return false;
          const dueDate = parseLocalDate(item.due_date);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate < today;
        });

        const dueSoon = items.filter(item => {
          if (item.completed || item.completion_status === 'completed') return false;
          if (!item.due_date) return false;
          const dueDate = parseLocalDate(item.due_date);
          dueDate.setHours(0, 0, 0, 0);
          const daysUntil = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
          return daysUntil >= 0 && daysUntil <= 7;
        });

        const completed = items.filter(item =>
          item.completed || item.completion_status === 'completed'
        );

        const total = items.length;
        const completedCount = completed.length;
        const overdueCount = overdue.length;
        const dueSoonCount = dueSoon.length;

        // Calculate health score (0-100)
        let healthScore = 100;

        // Deduct for overdue tasks
        healthScore -= (overdueCount * 15);

        // Deduct for low completion rate
        const completionRate = total > 0 ? completedCount / total : 1;
        if (completionRate < 0.3) healthScore -= 20;
        else if (completionRate < 0.5) healthScore -= 10;

        // Deduct for high due-soon count
        if (dueSoonCount > 5) healthScore -= 15;
        else if (dueSoonCount > 3) healthScore -= 10;

        healthScore = Math.max(0, Math.min(100, healthScore));

        // Determine pressure level
        let pressure = 'Normal';
        if (overdueCount > 3 || (overdueCount > 0 && dueSoonCount > 5)) {
          pressure = 'High';
        } else if (overdueCount > 0 || dueSoonCount > 3) {
          pressure = 'Moderate';
        }

        setTimelineHealth({
          healthScore,
          pressure,
          tasksCompleted: `${completedCount}/${total}`,
          overdue: overdueCount,
          dueSoon: dueSoonCount,
          total
        });

      } catch (error) {
        console.error('Error loading timeline health:', error);
      } finally {
        setLoadingTimeline(false);
      }
    };

    loadTimelineHealth();
  }, [currentUser?.id, collegeId, applicationComponents]); // Re-calculate when components change

  // Load existing Value Proposition data
  useEffect(() => {
    if (mergedCollegeData) {
      setValueProposition({
        excitement: mergedCollegeData.value_proposition_excitement || '',
        growth: mergedCollegeData.value_proposition_growth || '',
        unique: mergedCollegeData.value_proposition_unique || ''
      });
    }
  }, [mergedCollegeData]);

  // Load existing Strategic Research data
  useEffect(() => {
    if (mergedCollegeData) {
      setStrategicResearch({
        programs: mergedCollegeData.strategic_research_programs || '',
        campus: mergedCollegeData.strategic_research_campus || '',
        culture: mergedCollegeData.strategic_research_culture || ''
      });
    }
  }, [mergedCollegeData]);

  // Load verification status when college changes
  useEffect(() => {
    if (collegeId && getVerificationStatus) {
      getVerificationStatus(collegeId).then(status => {
        setVerificationStatus(status);
      });
    }
  }, [collegeId, getVerificationStatus]);

  // Load essay slots for this college
  useEffect(() => {
    const loadEssaySlots = async () => {
      if (!collegeId || !currentUser?.id) return;

      setLoadingEssaySlots(true);
      try {
        const { data, error } = await supabase
          .from('essay_slots')
          .select('*')
          .eq('college_id', collegeId)
          .eq('user_id', currentUser.id)
          .order('slot_type', { ascending: true })
          .order('essay_slot', { ascending: true });

        if (error) {
          console.error('Error loading essay slots:', error);
        } else {
          setEssaySlots(data || []);
        }
      } catch (error) {
        console.error('Error loading essay slots:', error);
      } finally {
        setLoadingEssaySlots(false);
      }
    };

    loadEssaySlots();
  }, [collegeId, currentUser?.id]);

  // Auto-detect "In Progress" status based on essay slots activity
  useEffect(() => {
    if (!essaySlots || essaySlots.length === 0 || !college) return;

    // Only auto-upgrade from "planning" to "in_progress"
    // Don't downgrade if already submitted or further along
    if (applicationStatus !== 'planning') return;

    const hasActiveWork = essaySlots.some(slot =>
      slot.status === 'brainstorming' ||
      slot.status === 'drafting' ||
      slot.status === 'revising' ||
      slot.status === 'review' ||
      (slot.completion_percentage && slot.completion_percentage > 0 && slot.completion_percentage < 100)
    );

    if (hasActiveWork) {
      console.log('[CollegeStrategyV2] Auto-detected in-progress status from essay slots');
      handleStatusChange('in_progress', true); // true = auto-detected
    }
  }, [essaySlots, applicationStatus, college]);

  // Load one-off tasks from college metadata
  useEffect(() => {
    if (college?.one_off_tasks) {
      setOneOffTasks(college.one_off_tasks);
    } else {
      setOneOffTasks([]);
    }
  }, [college]);

  // Load application notes from college metadata
  useEffect(() => {
    if (college?.application_notes) {
      setApplicationNotes(college.application_notes);
    } else {
      setApplicationNotes('');
    }
  }, [college]);

  // ============ STUDENT VERIFICATION HANDLERS ============
  const handleEditDeadline = (deadlineType, currentValue) => {
    setEditingDeadline(deadlineType);
    setEditingDeadlineValue(currentValue || '');
  };

  const handleSaveDeadline = async (deadlineType) => {
    if (!editingDeadlineValue) {
      setEditingDeadline(null);
      return;
    }

    try {
      const result = await updateStudentDeadline(collegeId, deadlineType, editingDeadlineValue);
      if (result.success) {
        toast.success(`Deadline updated! ${result.updatedTasksCount} timeline tasks updated.`);
        // Refresh verification status
        const status = await getVerificationStatus(collegeId);
        setVerificationStatus(status);
        setEditingDeadline(null);
        setEditingDeadlineValue('');

        // Reload college data to show updated deadline
        const { data: updatedCollege } = await supabase
          .from('college_journey_colleges')
          .select('*')
          .eq('id', collegeId)
          .single();
        if (updatedCollege) {
          setCollege(updatedCollege);
        }
      } else {
        toast.error(`Failed to update deadline: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving deadline:', error);
      toast.error('Failed to save deadline. Please try again.');
    }
  };

  const handleCancelEditDeadline = () => {
    setEditingDeadline(null);
    setEditingDeadlineValue('');
  };

  // ============ ESSAY EDITING HANDLERS ============
  const handleEditEssays = () => {
    // Initialize edited essays from current essay slots
    // If no essay slots exist, initialize from global college essay requirements
    if (essaySlots.length > 0) {
      setEditedEssays(essaySlots.map(slot => ({
        id: slot.id,
        slot_type: slot.slot_type,
        essay_slot: slot.essay_slot,
        prompt_text: slot.prompt_text,
        word_limit: slot.word_limit,
        friendly_title: slot.friendly_title || '',
        is_optional: slot.is_optional || false
      })));
    } else if (mergedCollegeData?.essay_requirements?.essays) {
      // Initialize from global college data
      setEditedEssays(mergedCollegeData.essay_requirements.essays.map((essay, idx) => ({
        slot_type: 'supplemental',
        essay_slot: idx + 1,
        prompt_text: essay.prompt || '',
        word_limit: parseInt(essay.wordLimit) || 650,
        friendly_title: '',
        is_optional: false
      })));
    } else {
      setEditedEssays([]);
    }
    setEditingEssays(true);
  };

  const handleAddEssay = () => {
    setEditedEssays(prev => [
      ...prev,
      {
        slot_type: 'supplemental',
        essay_slot: prev.filter(e => e.slot_type === 'supplemental').length + 1,
        prompt_text: '',
        word_limit: 650,
        friendly_title: '',
        is_optional: false
      }
    ]);
  };

  const handleUpdateEssay = (index, field, value) => {
    setEditedEssays(prev => prev.map((essay, idx) =>
      idx === index ? { ...essay, [field]: value } : essay
    ));
  };

  const handleDeleteEssay = (index) => {
    setEditedEssays(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveEssays = async () => {
    try {
      const result = await updateStudentEssayPrompts(collegeId, editedEssays);
      if (result.success) {
        // Build detailed success message
        const changes = [];
        if (result.added > 0) changes.push(`${result.added} added`);
        if (result.updated > 0) changes.push(`${result.updated} updated`);
        if (result.deleted > 0) changes.push(`${result.deleted} deleted`);

        const message = changes.length > 0
          ? `✓ Essays saved successfully! ${changes.join(', ')}`
          : '✓ Essays saved successfully!';

        toast.success(message, { duration: 4000 });

        // Refresh verification status
        const status = await getVerificationStatus(collegeId);
        setVerificationStatus(status);
        // Reload essay slots
        const { data } = await supabase
          .from('essay_slots')
          .select('*')
          .eq('college_id', collegeId)
          .eq('user_id', currentUser.id)
          .order('slot_type', { ascending: true })
          .order('essay_slot', { ascending: true });
        setEssaySlots(data || []);
        setEditingEssays(false);
        setEditedEssays([]);
        // Reload college data to refresh timeline
        await loadCollegeJourneyData();
      } else {
        toast.error(`✗ Failed to update essays: ${result.error}`, { duration: 5000 });
      }
    } catch (error) {
      console.error('Error saving essays:', error);
      toast.error('✗ Failed to save essay changes. Please try again.', { duration: 5000 });
    }
  };

  const handleCancelEssayEditing = () => {
    setEditingEssays(false);
    setEditedEssays([]);
  };

  // ============ REQUIREMENTS HANDLERS ============
  const handleEditRequirements = () => {
    // Initialize from override if exists, otherwise from database components
    if (college?.application_requirements_override && Array.isArray(college.application_requirements_override)) {
      setEditedRequirements(college.application_requirements_override);
    } else if (databaseApplicationComponents.length > 0) {
      // Convert database components to editable format
      const requirements = databaseApplicationComponents.map(comp => ({
        name: comp.name,
        details: comp.details || '',
        is_optional: comp.is_optional || false
      }));
      setEditedRequirements(requirements);
    } else {
      setEditedRequirements([]);
    }
    setEditingRequirements(true);
  };

  const handleAddRequirement = () => {
    setEditedRequirements(prev => [
      ...prev,
      {
        name: '',
        details: '',
        is_optional: false
      }
    ]);
  };

  const handleUpdateRequirement = (index, field, value) => {
    setEditedRequirements(prev => prev.map((req, idx) =>
      idx === index ? { ...req, [field]: value } : req
    ));
  };

  const handleRemoveRequirement = (index) => {
    setEditedRequirements(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveRequirements = async () => {
    try {
      // Filter out empty requirements
      const validRequirements = editedRequirements.filter(req => req.name.trim());

      const result = await updateStudentRequirements(collegeId, validRequirements);
      if (result.success) {
        toast.success('✓ Requirements updated successfully!', { duration: 4000 });
        const status = await getVerificationStatus(collegeId);
        setVerificationStatus(status);
        setEditingRequirements(false);
        await loadCollegeJourneyData();
      } else {
        toast.error(`✗ Failed to update requirements: ${result.error}`, { duration: 5000 });
      }
    } catch (error) {
      console.error('Error saving requirements:', error);
      toast.error('✗ Failed to save requirements. Please try again.', { duration: 5000 });
    }
  };

  const handleCancelRequirementsEditing = () => {
    setEditingRequirements(false);
    setEditedRequirements([]);
  };

  // ============ APPLICATION NOTES HANDLERS ============
  const handleEditApplicationNotes = () => {
    setEditedApplicationNotes(applicationNotes);
    setEditingApplicationNotes(true);
  };

  const handleSaveApplicationNotes = async () => {
    try {
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ application_notes: editedApplicationNotes })
        .eq('id', collegeId);

      if (error) throw error;

      toast.success('✓ Application notes saved successfully!', { duration: 4000 });
      setApplicationNotes(editedApplicationNotes);
      setEditingApplicationNotes(false);
      await loadCollegeJourneyData();
    } catch (error) {
      console.error('Error saving application notes:', error);
      toast.error('✗ Failed to save notes. Please try again.', { duration: 5000 });
    }
  };

  const handleCancelApplicationNotesEditing = () => {
    setEditingApplicationNotes(false);
    setEditedApplicationNotes('');
  };

  // ============ ONE-OFF TASK HANDLERS ============
  const handleEditOneOffTasks = () => {
    // Load existing one-off tasks from metadata or initialize empty
    const existingTasks = college?.one_off_tasks || [];
    setEditedOneOffTasks(existingTasks);
    setEditingOneOffTasks(true);
  };

  const handleAddOneOffTask = () => {
    setEditedOneOffTasks(prev => [
      ...prev,
      {
        title: '',
        description: '',
        category: 'application'
      }
    ]);
  };

  const handleUpdateOneOffTask = (index, field, value) => {
    setEditedOneOffTasks(prev => prev.map((task, idx) =>
      idx === index ? { ...task, [field]: value } : task
    ));
  };

  const handleDeleteOneOffTask = (index) => {
    setEditedOneOffTasks(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveOneOffTasks = async () => {
    try {
      // Save to college metadata
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ one_off_tasks: editedOneOffTasks })
        .eq('id', collegeId);

      if (error) throw error;

      toast.success('✓ Special tasks saved successfully!', { duration: 4000 });
      setOneOffTasks(editedOneOffTasks);
      setEditingOneOffTasks(false);
      await loadCollegeJourneyData();
    } catch (error) {
      console.error('Error saving one-off tasks:', error);
      toast.error('✗ Failed to save special tasks. Please try again.', { duration: 5000 });
    }
  };

  const handleCancelOneOffTasksEditing = () => {
    setEditingOneOffTasks(false);
    setEditedOneOffTasks([]);
  };

  // Toggle task status for application components
  const toggleTaskStatus = async (taskId) => {
    const task = applicationComponents.find(t => t.id === taskId);
    if (!task) return;

    const statusOrder = [
      TASK_STATUS.NOT_STARTED,
      TASK_STATUS.IN_PROGRESS,
      TASK_STATUS.BLOCKED,
      TASK_STATUS.COMPLETED
    ];

    const currentIndex = statusOrder.indexOf(task.status || TASK_STATUS.NOT_STARTED);
    const nextIndex = (currentIndex + 1) % statusOrder.length;
    const newStatus = statusOrder[nextIndex];

    // Update locally first for immediate feedback
    setApplicationComponents(prev => prev.map(t =>
      t.id === taskId ? {
        ...t,
        status: newStatus,
        completed: newStatus === TASK_STATUS.COMPLETED
      } : t
    ));

    // Update in database
    try {
      const updates = {
        completion_status: newStatus === TASK_STATUS.COMPLETED ? 'completed' :
                          newStatus === TASK_STATUS.IN_PROGRESS ? 'in_progress' :
                          'not_started',
        status: newStatus,
        completed: newStatus === TASK_STATUS.COMPLETED,
        completion_date: newStatus === TASK_STATUS.COMPLETED ? new Date().toISOString() : null
      };

      await timelineService.upsertTimelineItem({
        id: taskId,
        ...updates
      });

      if (newStatus === TASK_STATUS.COMPLETED) {
        toast.success('Component completed!');
      }
    } catch (error) {
      console.error('Error updating component status:', error);
      toast.error('Failed to update component status');
      // Reload components to restore correct state
      const { data } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', currentUser.id)
        .eq('college_id', collegeId)
        .eq('source_type', 'application_component')
        .order('created_at', { ascending: true });
      setApplicationComponents(data || []);
    }
  };

  // Handle Value Proposition textarea changes with debounced save
  const handleValuePropChange = async (field, value) => {
    // Update local state immediately
    setValueProposition(prev => ({
      ...prev,
      [field]: value
    }));

    // Debounce database save
    clearTimeout(window.valuePropSaveTimeout);
    window.valuePropSaveTimeout = setTimeout(async () => {
      try {
        const { error } = await supabase
          .from('college_journey_colleges')
          .update({
            [`value_proposition_${field}`]: value
          })
          .eq('id', collegeId);

        if (error) throw error;
      } catch (error) {
        console.error('Error saving value proposition:', error);
        toast.error('Failed to save');
      }
    }, 1000); // Save 1 second after user stops typing
  };

  // Generate AI value proposition
  const handleGenerateValueProposition = async () => {
    setGeneratingValueProp(true);
    try {
      const response = await fetch('/api/anthropic/strategic-features', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-value-proposition',
          userId: currentUser.id,
          collegeId: collegeId,
          collegeName: mergedCollegeData.name,
          collegeData: mergedCollegeData
        })
      });

      const data = await response.json();

      if (data.success && data.valueProposition) {
        // Save the suggestion to the college's ai_suggestions field
        const currentSuggestions = mergedCollegeData.ai_suggestions || {};
        const updatedSuggestions = {
          ...currentSuggestions,
          value_proposition: data.valueProposition
        };

        const { error: saveError } = await supabase
          .from('college_journey_colleges')
          .update({
            ai_suggestions: updatedSuggestions
          })
          .eq('id', collegeId);

        if (saveError) {
          console.error('Error saving suggestions:', saveError);
        }

        // Update local state
        setCollege(prev => ({
          ...prev,
          ai_suggestions: updatedSuggestions
        }));

        toast.success('Value proposition suggestions generated!');
      } else {
        throw new Error(data.error || 'Failed to generate value proposition');
      }
    } catch (error) {
      console.error('Error generating value proposition:', error);
      toast.error('Failed to generate value proposition');
    } finally {
      setGeneratingValueProp(false);
    }
  };

  // Handle Strategic Research textarea changes with debounced save
  const handleStrategicResearchChange = async (field, value) => {
    // Update local state immediately
    setStrategicResearch(prev => ({
      ...prev,
      [field]: value
    }));

    // Debounce database save
    clearTimeout(window.researchSaveTimeout);
    window.researchSaveTimeout = setTimeout(async () => {
      try {
        const { error } = await supabase
          .from('college_journey_colleges')
          .update({
            [`strategic_research_${field}`]: value
          })
          .eq('id', collegeId);

        if (error) throw error;
      } catch (error) {
        console.error('Error saving strategic research:', error);
        toast.error('Failed to save');
      }
    }, 1000);
  };

  // Generate AI strategic research
  const handleGenerateStrategicResearch = async () => {
    setGeneratingResearch(true);
    try {
      const response = await fetch('/api/anthropic/strategic-features', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-strategic-research',
          userId: currentUser.id,
          collegeId: collegeId,
          collegeName: mergedCollegeData.name,
          collegeData: mergedCollegeData
        })
      });

      const data = await response.json();

      if (data.success && data.strategicResearch) {
        // Save the suggestion to the college's ai_suggestions field
        const currentSuggestions = mergedCollegeData.ai_suggestions || {};
        const updatedSuggestions = {
          ...currentSuggestions,
          strategic_research: data.strategicResearch
        };

        const { error: saveError } = await supabase
          .from('college_journey_colleges')
          .update({
            ai_suggestions: updatedSuggestions
          })
          .eq('id', collegeId);

        if (saveError) {
          console.error('Error saving research suggestions:', saveError);
        }

        // Update local state
        setCollege(prev => ({
          ...prev,
          ai_suggestions: updatedSuggestions
        }));

        toast.success('Strategic research suggestions generated!');
      } else {
        throw new Error(data.error || 'Failed to generate strategic research');
      }
    } catch (error) {
      console.error('Error generating strategic research:', error);
      toast.error('Failed to generate strategic research');
    } finally {
      setGeneratingResearch(false);
    }
  };

  // Generate student advantages
  const handleGenerateStudentAdvantages = async () => {
    setGeneratingValueProp(true);
    try {
      // Fetch student academic profile
      const { data: profileData, error: profileError } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();

      if (profileError) {
        console.error('Error fetching student profile:', profileError);
      }

      // Build student profile object
      const studentProfile = profileData ? {
        gpa: profileData.gpa_weighted || profileData.gpa_unweighted,
        sat_score: profileData.sat_total,
        act_score: profileData.act_composite,
        extracurriculars: profileData.extracurriculars,
        achievements: profileData.awards || profileData.academic_awards,
        interests: profileData.academic_interests,
        intended_major: profileData.intended_major,
        career_goals: profileData.career_goals,
        unique_circumstances: profileData.special_circumstances,
        location: profileData.state_of_residency
      } : null;

      console.log('Student profile:', studentProfile);

      const response = await fetch('/api/anthropic/strategic-features', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-student-advantages',
          userId: currentUser.id,
          collegeId: collegeId,
          collegeName: mergedCollegeData.name,
          collegeData: mergedCollegeData,
          studentProfile: studentProfile
        })
      });

      const data = await response.json();

      console.log('API Response:', data);

      if (data.success && data.advantages) {
        console.log('Generated advantages:', data.advantages);

        // Update local state immediately for instant UI feedback
        setCollege(prev => ({
          ...prev,
          student_advantages: data.advantages
        }));

        // Save advantages to database
        console.log('Saving to database:', {
          collegeId,
          advantages: data.advantages,
          advantagesType: typeof data.advantages,
          advantagesLength: data.advantages?.length
        });

        const { data: updateResult, error } = await supabase
          .from('college_journey_colleges')
          .update({
            student_advantages: data.advantages
          })
          .eq('id', collegeId)
          .select();

        console.log('Database update result:', updateResult);
        console.log('Database update error:', error);

        if (error) {
          console.error('Error saving to database:', error);
          throw error;
        }

        toast.success('Strategic advantages generated!');

        // Refresh college data from database to ensure sync
        const { data: updatedCollege, error: refreshError } = await supabase
          .from('college_journey_colleges')
          .select('*')
          .eq('id', collegeId)
          .single();

        console.log('Refreshed college from DB:', {
          updatedCollege,
          student_advantages: updatedCollege?.student_advantages,
          advantagesType: typeof updatedCollege?.student_advantages,
          isArray: Array.isArray(updatedCollege?.student_advantages)
        });

        if (!refreshError && updatedCollege) {
          setCollege(updatedCollege);
        } else if (refreshError) {
          console.error('Error refreshing college data:', refreshError);
        }
      } else {
        console.error('API returned error:', data);
        throw new Error(data.error || 'Failed to generate advantages');
      }
    } catch (error) {
      console.error('Error generating student advantages:', error);
      console.error('Error details:', error.message);
      toast.error('Failed to generate strategic advantages');
    } finally {
      setGeneratingValueProp(false);
    }
  };

  // Generate improvement plan
  const handleGenerateImprovementPlan = async () => {
    setGeneratingImprovementPlan(true);
    try {
      // Fetch student timeline data
      const { data: profileData, error: profileError } = await supabase
        .from('student_academic_profiles')
        .select('current_grade, graduation_year')
        .eq('user_id', currentUser.id)
        .single();

      if (profileError) {
        console.log('No student profile found, using defaults');
      }

      // Calculate application cycle
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1; // 0-indexed

      // Determine graduation year
      const graduationYear = profileData?.graduation_year || (currentYear + 1);

      // Application year is senior year
      const applicationYear = graduationYear;
      const applicationCycle = `${applicationYear - 1}-${applicationYear}`;

      // Determine current grade if not in profile
      const currentGrade = profileData?.current_grade || 12;
      const isJunior = currentGrade === 11;
      const isSenior = currentGrade === 12;

      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-improvement-strategy',
          userId: currentUser.id,
          collegeId: collegeId,
          collegeName: mergedCollegeData.name,
          schoolType: college.category,
          timeline: {
            applicationRound: selectedApplicationRound,
            currentGrade,
            graduationYear,
            applicationCycle,
            isJunior,
            isSenior,
            currentDate: currentDate.toISOString().split('T')[0]
          },
          collegeData: mergedCollegeData
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Improvement plan generated!');

        // Refresh college data to get the new improvement plan
        const { data: updatedCollege, error } = await supabase
          .from('college_journey_colleges')
          .select('*')
          .eq('id', collegeId)
          .single();

        if (!error && updatedCollege) {
          setCollege(updatedCollege);
        }

        // Expand the immediate actions section
        setExpandedSections(prev => ({
          ...prev,
          immediateActions: true
        }));
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Error generating improvement plan:', error);
      toast.error('Failed to generate improvement plan');
    } finally {
      setGeneratingImprovementPlan(false);
    }
  };

  // Add improvement plan item to timeline tasks
  const handleAddToTasks = async (item, type = 'immediate') => {
    if (!currentUser?.id || !collegeId) return;

    try {
      const taskData = {
        user_id: currentUser.id,
        college_id: collegeId,
        title: item.action,
        description: item.why,
        source_type: type === 'immediate' ? 'improvement_immediate' : 'improvement_focus',
        status: 'not_started',
        completed: false,
        // Set due date based on priority (immediate = 7 days, focus = 30 days)
        due_date: type === 'immediate'
          ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      };

      await timelineService.upsertTimelineItem(taskData);
      toast.success('Added to your tasks!');

      // Refresh timeline data
      const { data } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', currentUser.id)
        .eq('college_id', collegeId);

      setApplicationComponents(data || []);
    } catch (error) {
      console.error('Error adding to tasks:', error);
      toast.error('Failed to add task');
    }
  };

  // Initialize application components if they don't exist
  const initializeApplicationComponents = async (components) => {
    if (!currentUser?.id || !collegeId) return;

    try {
      const timelineItems = components.map(comp => ({
        user_id: currentUser.id,
        college_id: collegeId,
        source_type: 'application_component',
        title: comp.name,
        description: comp.details,
        status: TASK_STATUS.NOT_STARTED,
        priority: comp.urgent ? 'high' : 'medium',
        category: 'application',
        due_date: comp.deadline ? parseLocalDate(comp.deadline).toISOString().split('T')[0] : null,
        scheduled_date: comp.deadline ? parseLocalDate(comp.deadline).toISOString().split('T')[0] : null,
        tags: comp.urgent ? ['urgent'] : []
      }));

      const { data, error } = await supabase
        .from('timeline_items')
        .insert(timelineItems)
        .select();

      if (error) throw error;

      setApplicationComponents(data || []);
    } catch (error) {
      console.error('Error initializing application components:', error);
    }
  };

  // Generate application timeline from database requirements
  const generateApplicationTimeline = async () => {
    if (!currentUser?.id || !collegeId || !globalCollegeData?.id) {
      toast.error('Missing required data to generate timeline');
      return;
    }

    try {
      // Get application deadline based on selected round
      let applicationDeadline = null;
      if (selectedApplicationRound === 'early_decision' || selectedApplicationRound === 'early_action') {
        applicationDeadline = globalCollegeData.ed_deadline || globalCollegeData.ea_deadline;
      } else if (selectedApplicationRound === 'early_decision_2') {
        applicationDeadline = globalCollegeData.ed2_deadline;
      } else {
        applicationDeadline = globalCollegeData.rd_deadline;
      }

      if (!applicationDeadline) {
        toast.error('No application deadline found for selected round');
        return;
      }

      toast.loading('Generating application timeline...', { id: 'generate-timeline' });

      const result = await ApplicationRequirementsService.generateApplicationTimeline(
        currentUser.id,
        collegeId,
        globalCollegeData.id,
        applicationDeadline
      );

      if (result.success) {
        toast.success(result.message || 'Application timeline generated!', { id: 'generate-timeline' });

        // Clear timeline_needs_generation flag and set timeline_auto_generated
        await supabase
          .from('college_journey_colleges')
          .update({
            timeline_auto_generated: true,
            timeline_needs_generation: false
          })
          .eq('id', collegeId);

        // Refresh application components
        const { data } = await supabase
          .from('timeline_items')
          .select('*')
          .eq('user_id', currentUser.id)
          .eq('college_id', collegeId)
          .eq('category', 'application');

        setApplicationComponents(data || []);

        // Refresh college data to update button visibility
        await refreshColleges();
      } else {
        toast.error(result.error || 'Failed to generate timeline', { id: 'generate-timeline' });
      }
    } catch (error) {
      console.error('Error generating application timeline:', error);
      toast.error('Failed to generate application timeline', { id: 'generate-timeline' });
    }
  };

  // StatusCheckbox component
  const StatusCheckbox = ({ task, onClick }) => {
    const statusIcons = {
      [TASK_STATUS.NOT_STARTED]: <Circle className="w-4 sm:w-5 h-4 sm:h-5 text-stone-400" />,
      [TASK_STATUS.IN_PROGRESS]: (
        <div className="relative">
          <Circle className="w-4 sm:w-5 h-4 sm:h-5 text-amber-500" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" />
          </div>
        </div>
      ),
      [TASK_STATUS.BLOCKED]: <PauseCircle className="w-4 sm:w-5 h-4 sm:h-5 text-red-500" />,
      [TASK_STATUS.COMPLETED]: <CheckCircle2 className="w-4 sm:w-5 h-4 sm:h-5 text-emerald-500" />
    };

    const currentStatus = task.status || TASK_STATUS.NOT_STARTED;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClick(task.id);
        }}
        className="flex-shrink-0 p-0.5 hover:bg-stone-50 rounded transition-colors"
        aria-label="Toggle task status"
      >
        {statusIcons[currentStatus]}
      </button>
    );
  };

  // ============ LOADING STATE ============
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-slate-700">Loading college strategy...</p>
        </div>
      </div>
    );
  }

  if (!college) {
    return (
      <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-indigo-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">College Not Found</h2>
          <p className="text-stone-600 mb-4">We couldn't find the college you're looking for.</p>
          <button
            onClick={() => navigate('/college')}
            className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all"
          >
            Back to College Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">

      {/* Requirements Fetch Loader */}
      {showFetchLoader && fetchCollegeData && (
        <RequirementsFetchLoader
          collegeId={fetchCollegeData.id}
          collegeName={fetchCollegeData.name}
          userId={currentUser?.id}
          graduationYear={studentGraduationYear}
          onComplete={handleFetchComplete}
          onCancel={handleFetchCancel}
        />
      )}

      {/* Note: Navigation header is handled by CollegeJourneyWrapper */}

      {/* Main Container for Navigation Bar and Content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 lg:pt-8">
        {/* College Navigation Bar */}
        <CollegeNavigationBar
          currentCollegeId={collegeId}
          colleges={colleges}
          onCollegeChange={(id) => navigate(`/college/colleges/${id}`)}
          onBackToGrid={() => navigate('/college/colleges')}
        />
      </div>

      {/* ============ COLLEGE HEADER ============ */}
      <div className="bg-slate-900 text-white border-b-2 border-slate-700">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
            <div className="flex items-start gap-3 sm:gap-4 flex-1">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2 truncate">{mergedCollegeData.name}</h1>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-stone-300 text-xs sm:text-sm">
                  {mergedCollegeData.location && (
                    <>
                      <span className="truncate">{mergedCollegeData.location}</span>
                      <span className="hidden sm:inline">•</span>
                    </>
                  )}
                  {mergedCollegeData.student_enrollment && (
                    <>
                      <span className="whitespace-nowrap">{mergedCollegeData.student_enrollment.toLocaleString()} students</span>
                      <span className="hidden sm:inline">•</span>
                    </>
                  )}
                  {mergedCollegeData.category && (
                    <span className={`px-2 py-0.5 ${getCategoryBadgeColor(mergedCollegeData.category)} text-xs sm:text-sm font-semibold rounded whitespace-nowrap`}>
                      {getCategoryLabel(mergedCollegeData.category)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Application Round Selector */}
            <div className="w-full sm:w-auto">
              <div className="bg-slate-800 rounded-lg px-3 sm:px-4 py-2 sm:py-3 border border-slate-700">
                <div className="text-stone-400 text-xs mb-1 uppercase tracking-wide">Application</div>
                <select
                  className="bg-slate-700 text-white px-2 sm:px-3 py-1 rounded border border-slate-600 text-xs sm:text-sm font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500 w-full"
                  value={selectedApplicationRound}
                  onChange={(e) => handleApplicationRoundChange(e.target.value)}
                >
                  <option value="regular">Regular Decision</option>
                  <option value="early_decision">Early Decision</option>
                  <option value="early_action">Early Action</option>
                  <option value="early_decision_2">Early Decision II</option>
                  <option value="rolling">Rolling Admission</option>
                </select>
              </div>
            </div>

            {/* Application Status Badge */}
            <div className="w-full sm:w-auto">
              <div className="bg-slate-800 rounded-lg px-3 sm:px-4 py-2 sm:py-3 border border-slate-700">
                <div className="text-stone-400 text-xs mb-1 uppercase tracking-wide">Status</div>
                <button
                  onClick={() => {
                    const statusFlow = ['planning', 'in_progress', 'submitted', 'decision_received'];
                    const currentIndex = statusFlow.indexOf(applicationStatus);
                    const nextIndex = (currentIndex + 1) % statusFlow.length;
                    handleStatusChange(statusFlow[nextIndex]);
                  }}
                  title="Click to advance status"
                  className={`px-2 sm:px-3 py-1 rounded border text-xs sm:text-sm font-semibold cursor-pointer transition-all w-full text-left focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    applicationStatus === 'planning'
                      ? 'bg-slate-700 border-slate-600 text-stone-400'
                      : applicationStatus === 'in_progress'
                      ? 'bg-amber-500/20 border-amber-500 text-amber-300'
                      : applicationStatus === 'submitted'
                      ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                      : applicationStatus === 'completed'
                      ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                      : 'bg-teal-500/20 border-teal-500 text-teal-300'
                  }`}
                >
                  {applicationStatus === 'planning' && '● Planning'}
                  {applicationStatus === 'in_progress' && '● In Progress'}
                  {applicationStatus === 'submitted' && '● Submitted'}
                  {applicationStatus === 'completed' && '● Completed'}
                  {applicationStatus === 'decision_received' && '● Decision'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ TAB NAVIGATION ============ */}
      <div className="bg-white border-b border-stone-200 overflow-x-auto">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-0 min-w-max sm:min-w-0">
            {[
              { id: 'strategy', label: 'Strategy & Fit', shortLabel: 'Strategy', badge: null },
              { id: 'actions', label: 'Action Items', shortLabel: 'Actions', badge: college?.improvement_plan?.immediate_actions?.filter(a => !a.completed).length || null },
              { id: 'application', label: 'Application Requirements', shortLabel: 'Application', badge: null },
              { id: 'research', label: 'School Research', shortLabel: 'Research', badge: null }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-3 sm:px-6 py-3 sm:py-4 font-medium text-xs sm:text-sm transition-all border-b-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-teal-700 border-teal-600 bg-teal-50/50'
                    : 'text-stone-600 border-transparent hover:text-slate-900 hover:bg-stone-50'
                }`}
              >
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
                {tab.badge && (
                  <span className="absolute top-1 sm:top-2 -right-0.5 sm:-right-1 min-w-[16px] sm:min-w-[20px] h-4 sm:h-5 px-1 sm:px-1.5 bg-indigo-400 text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center justify-center">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ============ URGENT DEADLINE WARNING (if within 45 days) ============ */}
      {criticalDeadlines.length > 0 && criticalDeadlines[0].daysUntil <= 45 && criticalDeadlines[0].daysUntil > 0 && (
        <div className="bg-gradient-to-r from-amber-50 via-orange-50/80 to-amber-50 border-b border-amber-200/60 shadow-sm">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-5">
            <div className="flex items-center gap-3 sm:gap-5">
              {/* Animated indicator */}
              <div className="relative flex items-center justify-center flex-shrink-0">
                <div className="absolute w-6 sm:w-8 h-6 sm:h-8 bg-amber-500/20 rounded-full animate-ping"></div>
                <div className="relative w-8 sm:w-10 h-8 sm:h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center shadow-md">
                  <svg className="w-4 sm:w-5 h-4 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2 mb-1">
                  <h3 className="text-sm sm:text-base font-bold text-slate-900 truncate">
                    {criticalDeadlines[0].label.replace('DEADLINE', 'Deadline')}
                  </h3>
                  <span className="text-xs sm:text-sm text-slate-600 truncate">for {mergedCollegeData.name}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                  <div className="flex items-center gap-1 sm:gap-1.5 text-amber-900">
                    <span className="font-medium">Due</span>
                    <span className="font-semibold">{criticalDeadlines[0].date}</span>
                  </div>
                  <span className="text-slate-400 hidden sm:inline">•</span>
                  <span className="font-semibold text-orange-700 whitespace-nowrap">
                    {criticalDeadlines[0].daysUntil} {criticalDeadlines[0].daysUntil === 1 ? 'day' : 'days'} remaining
                  </span>
                  <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] sm:text-xs font-semibold rounded-full shadow-sm whitespace-nowrap">
                    Official Deadline
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ TAB CONTENT ============ */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">

        {/* STRATEGY & FIT TAB */}
        {activeTab === 'strategy' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Main Content - Left 2/3 */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">

              {/* Intelligent Insights */}
              <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                <div className="bg-teal-50 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between border-b-2 border-slate-500">
                  <div className="flex items-center gap-2 sm:gap-2 sm:gap-3">
                    <Brain className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                    <h2 className="text-sm sm:text-sm sm:text-md font-bold text-slate-900">INTELLIGENT INSIGHTS</h2>
                  </div>
                  <button
                    onClick={handleGenerateInsights}
                    className="p-1.5 sm:p-2 hover:bg-stone-50 rounded transition-all"
                    title="Regenerate AI insights"
                  >
                    <RefreshCw className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-stone-600" />
                  </button>
                </div>

                <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                  {mergedCollegeData.ai_insights ? (
                    <>
                      {/* Parse and display AI insights */}
                      {(() => {
                        let insights = {};
                        try {
                          // Try to parse if it's a JSON string
                          insights = typeof mergedCollegeData.ai_insights === 'string'
                            ? JSON.parse(mergedCollegeData.ai_insights)
                            : mergedCollegeData.ai_insights;
                        } catch (e) {
                          // If parsing fails, display as plain text
                          return (
                            <div className="bg-stone-50 border border-stone-300 rounded-r-lg p-4">
                              <p className="text-slate-900 leading-relaxed text-sm">
                                {mergedCollegeData.ai_insights}
                              </p>
                            </div>
                          );
                        }

                        return (
                          <>
                            {insights.valuesMatch && (
                              <div className="bg-stone-50 border border-stone-300 rounded-r-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <Heart className="w-4 h-4 text-slate-900" />
                                  <span className="text-sm font-bold text-slate-900 uppercase tracking-wide">VALUES MATCH</span>
                                </div>
                                <p className="text-slate-900 leading-relaxed text-sm">
                                  {insights.valuesMatch}
                                </p>
                              </div>
                            )}

                            {insights.academicFit && (
                              <div className="bg-stone-50 border border-stone-300 rounded-r-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <GraduationCap className="w-4 h-4 text-slate-900" />
                                  <span className="text-sm font-bold text-slate-900 uppercase tracking-wide">ACADEMIC FIT</span>
                                </div>
                                <p className="text-slate-900 leading-relaxed text-sm">
                                  {insights.academicFit}
                                </p>
                              </div>
                            )}

                            {insights.locationAdvantages && (
                              <div className="bg-stone-50 border border-stone-300 rounded-r-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <MapPin className="w-4 h-4 text-teal-900" />
                                  <span className="text-sm font-bold text-teal-900 uppercase tracking-wide">LOCATION</span>
                                </div>
                                <p className="text-slate-900 leading-relaxed text-sm">
                                  {insights.locationAdvantages}
                                </p>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <Brain className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                      <p className="text-stone-600 text-sm mb-4">No AI insights generated yet</p>
                      <button
                        onClick={handleGenerateInsights}
                        className="px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-all text-sm"
                      >
                        <Sparkles className="w-4 h-4 inline mr-2" />
                        Generate Insights
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Strategic Edge / Student Advantages */}
              <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
              <div className="bg-teal-50 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between border-b-2 border-slate-500">
              <div className="flex items-center gap-2 sm:gap-3">
                    <Target className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                    <div>
                      <h2 className="text-sm sm:text-md font-bold text-slate-900">YOUR STRATEGIC EDGE</h2>
                    </div>
                  </div>
                  {/* Regenerate button - only show if advantages exist */}
                  {(() => {
                    let advantages = mergedCollegeData.student_advantages;
                    let hasAdvantages = false;

                    if (typeof advantages === 'string' && advantages.trim()) {
                      // Check if it's markdown text or try parsing as JSON
                      if (advantages.includes('##') || /^\d+\./.test(advantages.trim())) {
                        hasAdvantages = true;
                      } else {
                        try {
                          const parsed = JSON.parse(advantages);
                          hasAdvantages = Array.isArray(parsed) && parsed.length > 0;
                        } catch (e) {
                          // Invalid JSON but has content
                          hasAdvantages = false;
                        }
                      }
                    } else if (Array.isArray(advantages)) {
                      hasAdvantages = advantages.length > 0;
                    }

                    return hasAdvantages ? (
                      <button
                        onClick={handleGenerateStudentAdvantages}
                        disabled={generatingValueProp}
                        className="p-2 hover:bg-teal-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Regenerate strategic advantages"
                      >
                        <RefreshCw
                          className={`w-4 h-4 text-slate-700 ${generatingValueProp ? 'animate-spin' : ''}`}
                        />
                      </button>
                    ) : null;
                  })()}
                </div>

                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                  {(() => {
                    let advantages = mergedCollegeData.student_advantages;

                    // Check if it's a non-empty string (could be markdown or JSON)
                    if (typeof advantages === 'string' && advantages.trim()) {
                      // Try to parse as JSON first
                      try {
                        const parsed = JSON.parse(advantages);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                          // It's a valid JSON array
                          advantages = parsed;
                        }
                      } catch (e) {
                        // Not JSON - treat as markdown text
                        // Check if it looks like markdown (contains ## or numbered lists)
                        if (advantages.includes('##') || /^\d+\./.test(advantages.trim())) {
                          return (
                            <div className="prose prose-sm max-w-none prose-headings:text-slate-900 prose-p:text-slate-700 prose-strong:text-slate-900 prose-ul:text-slate-700 prose-li:text-slate-700">
                              <ReactMarkdown>{advantages}</ReactMarkdown>
                            </div>
                          );
                        }
                      }
                    }

                    // Handle array format (parsed JSON or native array)
                    return advantages && Array.isArray(advantages) && advantages.length > 0 ? (
                      <div className="space-y-4">
                        {advantages.map((advantage, idx) => {
                        // Handle both string and object advantages defensively
                        if (typeof advantage === 'string') {
                          return (
                            <div key={idx}>
                              <h4 className="font-bold text-slate-900 mb-2">
                                {idx + 1}. {advantage}
                              </h4>
                            </div>
                          );
                        }

                        // Handle object advantage with proper fallbacks
                        const title = advantage?.title || advantage?.advantage || `Advantage ${idx + 1}`;
                        const description = advantage?.description || advantage?.narrative || 'No description available';

                        return (
                          <div key={idx}>
                            <h4 className="font-bold text-slate-900 mb-2">
                              {idx + 1}. {title}
                            </h4>
                            <p className="text-stone-700 text-sm leading-relaxed">
                              {description}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Target className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                      <p className="text-stone-600 text-sm mb-4">No strategic advantages defined yet</p>
                      <button
                        onClick={handleGenerateStudentAdvantages}
                        disabled={generatingValueProp}
                        className="px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {generatingValueProp ? (
                          <>
                            <RefreshCw className="w-4 h-4 inline mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 inline mr-2" />
                            Generate Strategic Advantages
                          </>
                        )}
                      </button>
                    </div>
                  );
                  })()}
                </div>
              </div>
            </div>

            {/* Right Sidebar - 1/3 */}
            <div className="space-y-6">

              {/* Timeline Health */}
              <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                  <h3 className="font-bold text-slate-900">Timeline Health</h3>
                </div>
                <div className="p-4 space-y-2 text-sm">
                  {loadingTimeline ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                    </div>
                  ) : timelineHealth ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-stone-600">Health Score</span>
                        <span className={`font-bold ${
                          timelineHealth.healthScore >= 80 ? 'text-teal-500' :
                          timelineHealth.healthScore >= 60 ? 'text-teal-700' :
                          'text-teal-900'
                        }`}>
                          {timelineHealth.healthScore}/100
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600">Pressure</span>
                        <span className={`font-bold ${
                          timelineHealth.pressure === 'High' ? 'text-teal-500' :
                          timelineHealth.pressure === 'Moderate' ? 'text-teal-700' :
                          'text-teal-900'
                        }`}>
                          {timelineHealth.pressure}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600">Tasks Completed</span>
                        <span className="font-bold text-slate-900">{timelineHealth.tasksCompleted}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600">Overdue</span>
                        <span className={`font-bold ${timelineHealth.overdue > 0 ? 'text-teal-500' : 'text-stone-900'}`}>
                          {timelineHealth.overdue}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600">Due Soon</span>
                        <span className={`font-bold ${timelineHealth.dueSoon > 0 ? 'text-teal-500' : 'text-stone-900'}`}>
                          {timelineHealth.dueSoon}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4 text-stone-500 text-xs">
                      No timeline items yet
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Context */}
              <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                  <h3 className="font-bold text-slate-900">Quick Context</h3>
                </div>
                <div className="p-4 space-y-3 text-sm">
                  {quickContext ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-stone-600">Your fit strength</span>
                        <span className={`font-bold ${
                          quickContext.fitStrength.includes('Very Strong') ? 'text-teal-500' :
                          quickContext.fitStrength.includes('Strong') ? 'text-teal-700' :
                          quickContext.fitStrength.includes('Good') ? 'text-teal-900' :
                          'text-stone-700'
                        }`}>
                          {quickContext.fitStrength}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600">Admission likelihood</span>
                        <span className={`font-bold ${
                          quickContext.admissionLikelihood.includes('Strong') ? 'text-teal-500' :
                          quickContext.admissionLikelihood === 'Competitive' ? 'text-teal-700' :
                          quickContext.admissionLikelihood.includes('Highly') ? 'text-teal-900' :
                          'text-amber-700'
                        }`}>
                          {quickContext.admissionLikelihood}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600">Application round</span>
                        <span className="font-bold text-slate-900">{quickContext.applicationRound}</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4 text-stone-500 text-xs">
                      Loading context...
                    </div>
                  )}
                </div>
              </div>

              {/* What This School Values */}
              <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
              <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
              <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-slate-900" />
                    <h3 className="font-bold text-slate-900">What This School Values</h3>
                  </div>
                </div>
                <div className="p-4">
                  {mergedCollegeData.core_values || globalCollegeData?.core_values ? (
                    <div className="space-y-4">
                      <div className="text-sm text-stone-700">
                        {(() => {
                          const coreValues = mergedCollegeData.core_values || globalCollegeData?.core_values;
                          let valuesArray = [];

                          try {
                            // Try to parse if it's a JSON string
                            valuesArray = typeof coreValues === 'string'
                              ? JSON.parse(coreValues)
                              : coreValues;
                          } catch (e) {
                            // If parsing fails, treat as plain text
                            return <p className="leading-relaxed">{coreValues}</p>;
                          }

                          // If it's an array, render as bullet list
                          if (Array.isArray(valuesArray)) {
                            return (
                              <ul className="space-y-2">
                                {valuesArray.map((value, idx) => (
                                  <li key={idx} className="flex items-start gap-2 sm:gap-3">
                                    <span className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-2 flex-shrink-0"></span>
                                    <span className="leading-relaxed">{value}</span>
                                  </li>
                                ))}
                              </ul>
                            );
                          }

                          // Otherwise render as text
                          return <p className="leading-relaxed">{valuesArray}</p>;
                        })()}
                      </div>
                      <div className="p-3 bg-stone-50 rounded-lg border border-stone-300">
                        <p className="text-sm text-teal-900">
                          <Lightbulb className="w-3 h-3 inline mr-1" />
                          Align your essays with these values by demonstrating how you embody them through specific examples and experiences.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-stone-600">No core values data available</p>
                  )}
                </div>
              </div>

              {/* Compare Button */}
              <button
                onClick={() => navigate('/college/compare', { state: { preselectedCollege: collegeId } })}
                className="w-full p-4 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-all border border-slate-900 flex items-center justify-center gap-2"
              >
                <Users className="w-4 h-4" />
                Compare with Other Schools
              </button>
            </div>
          </div>
        )}

        {/* ACTION ITEMS TAB */}
        {activeTab === 'actions' && (
          <div className="grid grid-cols-3 gap-8">
            {/* Main Content - Left 2/3 */}
            <div className="col-span-2 space-y-6">

              {/* Improve Your Odds - Expandable */}
              {college?.improvement_plan && (
                <>
                  {/* Immediate Priorities */}
                  <div id="section-immediateActions" className="bg-azure-50 rounded-lg border border-slate-500 overflow-hidden">
                    <button
                      onClick={() => toggleSection('immediateActions')}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-azure-500/50 transition-all"
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Lightbulb className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        <div className="text-left">
                          <h2 className="text-sm sm:text-md font-bold text-slate-900">IMPROVE YOUR ODDS</h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateImprovementPlan();
                          }}
                          disabled={generatingImprovementPlan}
                          className="p-2 hover:bg-azure-600/20 rounded transition-all disabled:opacity-50"
                          title="Regenerate improvement plan"
                        >
                          <RefreshCw className={`w-4 h-4 text-slate-900 ${generatingImprovementPlan ? 'animate-spin' : ''}`} />
                        </button>
                        {expandedSections.immediateActions ? (
                          <ChevronDown className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        ) : (
                          <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        )}
                      </div>
                    </button>

                    {expandedSections.immediateActions && (
                      <div className="border-t-2 border-slate-900 p-6 bg-white">
                        <div className="flex items-center gap-2 mb-4">
                          <Clock className="w-4 h-4 text-slate-900" />
                          <h3 className="font-semibold text-sm text-slate-900">IMMEDIATE PRIORITIES (Next 4-6 weeks)</h3>
                        </div>

                        <div className="space-y-4">
                          {college.improvement_plan.immediatePriorities?.map((priority, idx) => (
                            <div key={idx} className="bg-stone-50 rounded-r-lg p-4">
                              <div className="flex items-start gap-2 sm:gap-3">
                                <div className="w-4 sm:w-5 h-4 sm:h-5 rounded-full bg-azure-600 flex items-center justify-center flex-shrink-0">
                                  <Check className="w-3 h-3 text-white stroke-[3]" />
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-sm text-slate-700 mb-2">
                                    {priority.action}
                                  </h4>
                                  <p className="text-xs text-stone-700 mb-3">
                                    {priority.why}
                                  </p>
                                  {priority.successMetric && (
                                    <div className="mb-3 border-l-4 border-azure-300 p-4 ">
                                      <p className="text-xs text-slate-900">
                                        <span className="font-bold mr-1">✓</span>
                                        <span className="italic">{priority.successMetric}</span>
                                      </p>
                                    </div>
                                  )}
                                  <button
                                    onClick={() => handleAddToTasks(priority, 'immediate')}
                                    className="px-3 py-1.5 bg-slate-900 text-white text-sm font-medium rounded hover:bg-slate-800 transition-all"
                                  >
                                    + Add to My Tasks
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Primary Development Focus */}
                  <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                    <button
                      onClick={() => toggleSection('primaryDevelopment')}
                      className="w-full bg-azure-50 px-6 py-4 flex items-center justify-between hover:bg-azure-500/50 transition-all"
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <TrendingUp className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        <h2 className="text-sm sm:text-md font-bold text-slate-900">PRIMARY DEVELOPMENT FOCUS</h2>
                      </div>
                      {expandedSections.primaryDevelopment ? (
                        <ChevronDown className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                      ) : (
                        <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                      )}
                    </button>

                    {expandedSections.primaryDevelopment && (
                      <div className="border-t-2 border-slate-900 p-6 space-y-4">
                        {college.improvement_plan.primaryFocus?.map((focus, idx) => (
                          <div key={idx} className="bg-stone-50 rounded-r-lg p-4">
                            <div className="flex items-start gap-2 sm:gap-3">
                              <div className="w-4 sm:w-5 h-4 sm:h-5 rounded-full bg-azure-600 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3 text-white stroke-[3]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-sm text-slate-700 mb-2">
                                  {focus.action}
                                </h4>
                                <p className="text-xs text-stone-700 mb-3">
                                  {focus.why}
                                </p>
                                {focus.checkpoint && (
                                  <div className="mb-3 border-l-4 border-azure-50 p-4 ">
                                    <p className="text-xs text-slate-900">
                                      <Calendar className="w-3 h-3 inline mr-1" />
                                      <span className="italic">{focus.checkpoint}</span>
                                    </p>
                                  </div>
                                )}
                                <button
                                  onClick={() => handleAddToTasks(focus, 'focus')}
                                  className="px-3 py-1.5 bg-slate-900 text-white text-sm font-medium rounded hover:bg-slate-800 transition-all"
                                >
                                  + Add to My Tasks
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* University-Specific Positioning (for reach schools) */}
                  {college.category?.toLowerCase() === 'reach' && college.improvement_plan.reachPositioning && (
                    <div id="section-positioning" className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                      <button
                        onClick={() => toggleSection('positioning')}
                        className="w-full bg-azure-50 px-6 py-4 flex items-center justify-between hover:bg-azure-500/50 transition-all"
                      >
                        <div className="flex items-center gap-2 sm:gap-3">
                          <Target className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                          <h2 className="text-sm sm:text-md font-bold text-slate-900 ">{mergedCollegeData.name} - SPECIFIC POSITIONING</h2>
                        </div>
                        {expandedSections.positioning ? (
                          <ChevronDown className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        ) : (
                          <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        )}
                      </button>

                      {expandedSections.positioning && (
                        <div className="border-t-2 border-azure-500 p-6 space-y-4">
                          {college.improvement_plan.reachPositioning.map((pos, idx) => (
                            <div key={idx} className="bg-stone-50 rounded-r-lg p-4">
                              <div className="flex items-start gap-2 sm:gap-3">
                                <div className="w-4 sm:w-5 h-4 sm:h-5 rounded-full bg-azure-600 flex items-center justify-center flex-shrink-0">
                                  <Check className="w-3 h-3 text-white stroke-[3]" />
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-sm text-slate-700 mb-2">
                                    {pos.action}
                                  </h4>
                                  <p className="text-xs text-stone-700 mb-3">
                                    {pos.why}
                                  </p>
                                  {pos.tactic && (
                                  <div className="mb-3 border-l-4 border-azure-300 p-4 ">
                                      <p className="text-xs text-slate-900">
                                        <Lightbulb className="w-3 h-3 inline mr-1" />
                                        <span className="italic">{pos.tactic}</span>
                                      </p>
                                    </div>
                                  )}
                                  <button className="px-3 py-1.5 bg-slate-900 text-white text-sm font-medium rounded hover:bg-slate-800 transition-all">
                                    📝 Add to Essay Notes
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* No Improvement Plan State */}
              {!college?.improvement_plan && (
                <div className="bg-gradient-to-br from-stone-50 to-pink-50 rounded-lg border border-slate-900 p-8 text-center">
                  <Sparkles className="w-16 h-16 text-slate-900 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">IMPROVE YOUR ODDS</h3>
                  <p className="text-stone-700 mb-6">
                    Get AI-powered strategic recommendations to strengthen your application to {mergedCollegeData.name}
                  </p>
                  <button
                    onClick={handleGenerateImprovementPlan}
                    disabled={generatingImprovementPlan}
                    className="px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-800 text-white font-bold rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                  >
                    {generatingImprovementPlan ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate Improvement Strategy
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Right Sidebar - Essays & Deadlines */}
            <div className="space-y-6">
              {/* Essays Section */}
              <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                <div className="bg-stone-50 border-b-2 border-slate-900 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-900" />
                    <h3 className="font-bold text-slate-900">Essays</h3>
                  </div>
                </div>

                <div className="p-4">
                  {mergedCollegeData.essay_requirements ? (
                    <>
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="w-4 h-4 text-slate-900" />
                        <span className="font-bold text-azure-900 text-sm">
                          {mergedCollegeData.essay_requirements.essays?.length || 0} slot{(mergedCollegeData.essay_requirements.essays?.length || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {mergedCollegeData.essay_requirements.essays && mergedCollegeData.essay_requirements.essays.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {mergedCollegeData.essay_requirements.essays.map((essay, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-3 bg-stone-50 rounded border border-stone-300">
                              <input type="checkbox" className="w-4 h-4 mt-1 rounded border-stone-400" />
                              <div className="flex-1">
                                <div className="font-medium text-slate-900 text-sm mb-1">
                                  {essay.type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || `Essay ${idx + 1}`}
                                </div>
                                {essay.wordLimit && (
                                  <div className="text-xs text-stone-600">
                                    {essay.wordLimit} words max
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        onClick={() => navigate('/college/essays')}
                        className="w-full px-3 py-2 bg-slate-900 text-white font-medium rounded hover:bg-slate-800 transition-all text-sm"
                      >
                        Go to Essays
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <FileText className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                      <p className="text-stone-600 text-xs mb-3">No essay requirements</p>
                      <button className="w-full px-3 py-2 bg-slate-900 text-white font-medium rounded hover:bg-slate-800 transition-all text-sm">
                        Add Requirements
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Critical Deadlines */}
              <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                <div className="bg-azure-700 border-b-2 border-slate-900 px-4 py-3">
                  <h3 className="font-bold text-white text-sm">Critical Deadlines</h3>
                </div>
                <div className="p-4 space-y-2">
                  {criticalDeadlines.length > 0 ? (
                    criticalDeadlines.map((deadline, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border ${
                          deadline.priority === 'high'
                            ? 'bg-slate-900 text-white border-slate-900'
                            : deadline.daysUntil <= 7
                            ? 'bg-stone-50 border-coral-400'
                            : 'bg-stone-50 border-stone-300'
                        }`}
                      >
                        <div className={`font-bold text-sm mb-1 ${
                          deadline.priority === 'high'
                            ? 'text-slate-300'
                            : deadline.daysUntil <= 7
                            ? 'text-indigo-700'
                            : 'text-stone-700'
                        }`}>
                          {deadline.date} ({deadline.daysUntil} day{deadline.daysUntil !== 1 ? 's' : ''})
                        </div>
                        <div className={`font-medium text-sm ${
                          deadline.priority === 'high' ? 'font-bold' : 'text-slate-900'
                        }`}>
                          {deadline.label}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-stone-500 text-xs">
                      No deadlines available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* APPLICATION REQUIREMENTS TAB */}
        {activeTab === 'application' && (
          <div className="space-y-8">

            {/* Application Overview Header */}
            <div className="bg-white text-slate-900 rounded-lg border border-slate-900 overflow-hidden">
              <div className="bg-indigo-50/25 border-b-2 border-indigo-500 px-6 py-4">
              
                <h2 className="text-sm sm:text-md font-bold">Application Overview</h2>
                <p className="text-sm text-slate-800 mt-1">Key deadlines and application components for {mergedCollegeData.name}</p>
              
              </div>
              <div className="grid grid-cols-5 gap-4 p-6">
                {/* RD Deadline - Editable */}
                {(globalCollegeData?.rd_deadline || college?.rd_deadline_manual) && (
                  <div className="text-center group relative">
                    {editingDeadline === 'rd_deadline' ? (
                      <div className="flex flex-col items-center gap-1">
                        <input
                          type="date"
                          value={editingDeadlineValue}
                          onChange={(e) => setEditingDeadlineValue(e.target.value)}
                          className="text-sm px-2 py-1 border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleSaveDeadline('rd_deadline')}
                            className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEditDeadline}
                            className="px-2 py-1 text-xs bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-xl font-bold text-indigo-600 mb-1">
                          {parseLocalDate(college?.rd_deadline_manual || globalCollegeData.rd_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="text-xs text-stone-400 uppercase tracking-wide flex items-center justify-center gap-1">
                          RD Deadline
                          {college?.rd_deadline_manual && (
                            <CheckCircle className="w-3 h-3 text-green-600" title="Manually verified" />
                          )}
                        </div>
                        <button
                          onClick={() => handleEditDeadline('rd_deadline', college?.rd_deadline_manual || globalCollegeData.rd_deadline)}
                          className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                          title="Edit deadline"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                )}
                {/* ED Deadline - Editable */}
                {(globalCollegeData?.ed_deadline || college?.ed_deadline_manual) && (
                  <div className="text-center group relative">
                    {editingDeadline === 'ed_deadline' ? (
                      <div className="flex flex-col items-center gap-1">
                        <input
                          type="date"
                          value={editingDeadlineValue}
                          onChange={(e) => setEditingDeadlineValue(e.target.value)}
                          className="text-sm px-2 py-1 border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleSaveDeadline('ed_deadline')}
                            className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEditDeadline}
                            className="px-2 py-1 text-xs bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-xl font-bold text-slate-900 mb-1">
                          {parseLocalDate(college?.ed_deadline_manual || globalCollegeData.ed_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="text-xs text-stone-400 uppercase tracking-wide flex items-center justify-center gap-1">
                          ED Deadline
                          {college?.ed_deadline_manual && (
                            <CheckCircle className="w-3 h-3 text-green-600" title="Manually verified" />
                          )}
                        </div>
                        <button
                          onClick={() => handleEditDeadline('ed_deadline', college?.ed_deadline_manual || globalCollegeData.ed_deadline)}
                          className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                          title="Edit deadline"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                )}
                {/* EA Deadline - Editable */}
                {(globalCollegeData?.ea_deadline || college?.ea_deadline_manual) && (
                  <div className="text-center group relative">
                    {editingDeadline === 'ea_deadline' ? (
                      <div className="flex flex-col items-center gap-1">
                        <input
                          type="date"
                          value={editingDeadlineValue}
                          onChange={(e) => setEditingDeadlineValue(e.target.value)}
                          className="text-sm px-2 py-1 border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleSaveDeadline('ea_deadline')}
                            className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEditDeadline}
                            className="px-2 py-1 text-xs bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-xl font-bold text-slate-900 mb-1">
                          {parseLocalDate(college?.ea_deadline_manual || globalCollegeData.ea_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="text-xs text-stone-400 uppercase tracking-wide flex items-center justify-center gap-1">
                          EA Deadline
                          {college?.ea_deadline_manual && (
                            <CheckCircle className="w-3 h-3 text-green-600" title="Manually verified" />
                          )}
                        </div>
                        <button
                          onClick={() => handleEditDeadline('ea_deadline', college?.ea_deadline_manual || globalCollegeData.ea_deadline)}
                          className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                          title="Edit deadline"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                )}
                {applicationCounts.numEssays > 0 && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-slate-900 mb-1">{applicationCounts.numEssays}</div>
                    <div className="text-xs text-stone-400 uppercase tracking-wide">Essays Required</div>
                  </div>
                )}
                {applicationCounts.numTeacherRecs > 0 && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-slate-900 mb-1">{applicationCounts.numTeacherRecs}</div>
                    <div className="text-xs text-stone-400 uppercase tracking-wide">Teacher Recs</div>
                  </div>
                )}
                <div className="text-center">
                  <div className="text-xl font-bold text-indigo-400 mb-1">37%</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">Complete</div>
                </div>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-3 gap-8">
              {/* Left Column - Main Requirements (2/3 width) */}
              <div className="col-span-2 space-y-6">

                {/* Application Components Checklist */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-indigo-50/25 border-b-2 border-indigo-500 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <CheckCircle className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        <h2 className="text-sm sm:text-md font-bold text-slate-900">Application Components</h2>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm sm:text-md font-bold text-slate-900">{completionPercentage}% Complete</span>
                        {!editingRequirements && (
                          <button
                            onClick={handleEditRequirements}
                            className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1"
                            title="Edit requirements"
                          >
                            <Edit3 className="w-3 h-3" />
                            Edit
                          </button>
                        )}
                        {/* Show Generate Timeline button if: timeline needs generation OR has components but no timeline items yet */}
                        {(college?.timeline_needs_generation || (databaseApplicationComponents.length > 0 && !applicationComponentsList[0]?.timelineItemExists)) && !college?.timeline_auto_generated && (
                          <button
                            onClick={generateApplicationTimeline}
                            className="text-xs px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors flex items-center gap-1"
                            title={college?.timeline_needs_generation ? "Add deadline to generate your application timeline" : "Generate timeline from college-specific requirements"}
                          >
                            <Sparkles className="w-3 h-3" />
                            Generate Timeline
                          </button>
                        )}
                        {applicationComponentsList.length > 0 && !applicationComponentsList[0]?.timelineItemExists && databaseApplicationComponents.length === 0 && (
                          <button
                            onClick={() => initializeApplicationComponents(applicationComponentsList)}
                            className="text-xs px-3 py-1 bg-indigo-700 text-white rounded hover:bg-indigo-800 transition-colors"
                          >
                            Enable Tracking
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-3">
                    {editingRequirements ? (
                      // EDITING MODE
                      <div className="space-y-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-bold text-slate-900">Application Requirements</h4>
                          <button
                            onClick={handleAddRequirement}
                            className="text-xs px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" />
                            Add Requirement
                          </button>
                        </div>

                        {editedRequirements.map((req, idx) => (
                          <div key={idx} className={`rounded-lg p-4 border-2 ${
                            req.is_optional ? 'bg-stone-50 border-stone-300' : 'bg-indigo-50/25 border-indigo-200'
                          }`}>
                            <div className="space-y-3">
                              <div className="flex items-start gap-2">
                                <div className="flex-1">
                                  <label className="text-xs text-stone-600 font-medium">Requirement Name</label>
                                  <input
                                    type="text"
                                    value={req.name}
                                    onChange={(e) => handleUpdateRequirement(idx, 'name', e.target.value)}
                                    className="w-full mt-1 p-2 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder='e.g., "Teacher Recommendations (2)"'
                                  />
                                </div>
                                <div className="flex items-center gap-2 pt-5">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={req.is_optional}
                                      onChange={(e) => handleUpdateRequirement(idx, 'is_optional', e.target.checked)}
                                      className="w-4 h-4 text-indigo-600 border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <span className="text-xs text-stone-700 font-medium whitespace-nowrap">Optional</span>
                                  </label>
                                </div>
                              </div>

                              <div>
                                <label className="text-xs text-stone-600 font-medium">Details / Instructions</label>
                                <textarea
                                  value={req.details}
                                  onChange={(e) => handleUpdateRequirement(idx, 'details', e.target.value)}
                                  className="w-full mt-1 p-2 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  rows="2"
                                  placeholder="Additional details, deadlines, or instructions..."
                                />
                              </div>

                              <button
                                onClick={() => handleRemoveRequirement(idx)}
                                className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                              >
                                <X className="w-3 h-3" />
                                Remove Requirement
                              </button>
                            </div>
                          </div>
                        ))}

                        {editedRequirements.length === 0 && (
                          <div className="text-center py-8 text-stone-500 italic">
                            No requirements yet. Click "Add Requirement" to start building your list.
                          </div>
                        )}

                        <div className="flex gap-3 pt-4 border-t">
                          <button
                            onClick={handleSaveRequirements}
                            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" />
                            Save Requirements
                          </button>
                          <button
                            onClick={handleCancelRequirementsEditing}
                            className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // VIEWING MODE
                      <>
                        {applicationComponentsList.map((item, idx) => (
                          <div
                            key={item.id || idx}
                            className={`rounded-lg border p-4 transition-all ${
                              item.status === TASK_STATUS.COMPLETED ? 'bg-slate-50 border-slate-200' :
                              item.urgent ? 'bg-indigo-50/10 border-indigo-200' :
                              'bg-stone-50 border-stone-300'
                            }`}
                          >
                            <div className="flex items-start gap-2 sm:gap-3">
                              {item.id ? (
                                <StatusCheckbox task={item} onClick={toggleTaskStatus} />
                              ) : (
                                <div className="mt-1">
                                  <Circle className="w-4 sm:w-5 h-4 sm:h-5 text-stone-400" />
                                </div>
                              )}

                              <div className="flex-1">
                                <div className="flex items-start justify-between mb-1">
                                  <h3 className={`font-bold ${
                                    item.status === TASK_STATUS.COMPLETED ? 'text-teal-700 line-through' : 'text-slate-900'
                                  }`}>
                                    {item.name}
                                  </h3>
                                </div>

                                <p className="text-sm text-stone-700 mb-1">{item.details}</p>

                                {item.deadline && item.status !== TASK_STATUS.COMPLETED && (
                                  <div className={`flex items-center gap-1 text-xs font-medium ${
                                    item.urgent ? 'text-indigo-700' : 'text-stone-600'
                                  }`}>
                                    <Clock className="w-3 h-3" />
                                    <span>Due: {item.deadline}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>

                {/* Application Notes */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-blue-50/25 border-b-2 border-blue-500 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <FileText className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        <div>
                          <h2 className="text-sm sm:text-md font-bold text-slate-900">Application Notes</h2>
                          <p className="text-xs text-stone-600">Important details about this college's application process</p>
                        </div>
                      </div>
                      {!editingApplicationNotes && (
                        <button
                          onClick={handleEditApplicationNotes}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                        >
                          <Edit3 className="w-3 h-3" />
                          {applicationNotes ? 'Edit Notes' : 'Add Notes'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="p-6">
                    {editingApplicationNotes ? (
                      // EDITING MODE
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm text-stone-700 font-medium mb-2 block">
                            Notes about application process, unique requirements, or important reminders
                          </label>
                          <textarea
                            value={editedApplicationNotes}
                            onChange={(e) => setEditedApplicationNotes(e.target.value)}
                            className="w-full p-3 text-sm border-2 border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows="6"
                            placeholder="Example: This college requires SAT Subject Tests for engineering applicants. Portfolio must be submitted by Dec 1st. Interview is highly recommended but optional..."
                          />
                        </div>

                        <div className="flex gap-3 pt-2">
                          <button
                            onClick={handleSaveApplicationNotes}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" />
                            Save Notes
                          </button>
                          <button
                            onClick={handleCancelApplicationNotesEditing}
                            className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // VIEWING MODE
                      <>
                        {applicationNotes ? (
                          <div className="bg-blue-50/10 rounded-lg p-4 border border-blue-200">
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{applicationNotes}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-stone-500 italic text-center py-4">
                            No application notes yet. Click "Add Notes" to add important reminders or details about this college's application process.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Special/One-Off Tasks */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-amber-50/25 border-b-2 border-amber-500 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <AlertCircle className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        <div>
                          <h2 className="text-sm sm:text-md font-bold text-slate-900">Special Requirements</h2>
                          <p className="text-xs text-stone-600">College-specific unique tasks (e.g., The Rice Box)</p>
                        </div>
                      </div>
                      {!editingOneOffTasks && (
                        <button
                          onClick={handleEditOneOffTasks}
                          className="px-3 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center gap-1"
                        >
                          <Edit3 className="w-3 h-3" />
                          {oneOffTasks.length > 0 ? 'Edit' : 'Add Special Tasks'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="p-6 space-y-3">
                    {editingOneOffTasks ? (
                      // EDITING MODE
                      <div className="space-y-4">
                        {editedOneOffTasks.map((task, idx) => (
                          <div key={idx} className="bg-amber-50/25 rounded-lg p-4 border border-amber-200">
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-stone-600 font-medium">Task Title</label>
                                <input
                                  type="text"
                                  value={task.title}
                                  onChange={(e) => handleUpdateOneOffTask(idx, 'title', e.target.value)}
                                  className="w-full mt-1 p-2 text-sm border border-amber-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder='e.g., "The Rice Box"'
                                />
                              </div>
                              <div>
                                <label className="text-xs text-stone-600 font-medium">Description</label>
                                <textarea
                                  value={task.description}
                                  onChange={(e) => handleUpdateOneOffTask(idx, 'description', e.target.value)}
                                  className="w-full mt-1 p-2 text-sm border border-amber-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  rows="2"
                                  placeholder="In keeping with Rice's long-standing tradition, share an image of something that appeals to you."
                                />
                              </div>
                              <button
                                onClick={() => handleDeleteOneOffTask(idx)}
                                className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                              >
                                <X className="w-3 h-3" />
                                Remove Task
                              </button>
                            </div>
                          </div>
                        ))}

                        <button
                          onClick={handleAddOneOffTask}
                          className="w-full py-2 border-2 border-dashed border-amber-300 rounded text-amber-700 hover:bg-amber-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Special Task
                        </button>

                        <div className="flex gap-3 pt-4 border-t">
                          <button
                            onClick={handleSaveOneOffTasks}
                            className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" />
                            Save Special Tasks
                          </button>
                          <button
                            onClick={handleCancelOneOffTasksEditing}
                            className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // VIEWING MODE
                      <>
                        {oneOffTasks.length > 0 ? (
                          oneOffTasks.map((task, idx) => (
                            <div key={idx} className="rounded-lg border border-amber-200 bg-amber-50/10 p-4">
                              <div className="flex items-start gap-2 sm:gap-3">
                                <Circle className="w-4 sm:w-5 h-4 sm:h-5 text-amber-600 mt-1" />
                                <div className="flex-1">
                                  <h3 className="font-bold text-slate-900">{task.title}</h3>
                                  <p className="text-sm text-stone-700 mt-1">{task.description}</p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-stone-500 italic text-center py-4">
                            No special requirements. Click "Add Special Tasks" to add unique college-specific tasks.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Essay Requirements - Expandable */}
                {(mergedCollegeData.essay_requirements || essaySlots.length > 0) && (
                  <div id="section-essayPrompts" className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                    <button
                      onClick={() => toggleSection('essayPrompts')}
                      className="w-full bg-indigo-50/25 border-b-2 border-indigo-500 px-6 py-4 flex justify-between hover:bg-indigo-100 transition-all "
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <FileText className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        <div className="text-left">
                          <h2 className="text-sm sm:text-md font-bold text-slate-900">Essay Requirements & Prompts</h2>
                          <p className="text-sm text-stone-600">
                            View full essay prompts and requirements
                            {verificationStatus.essays && (
                              <span className="ml-2 text-green-600 font-medium">
                                <CheckCircle className="w-3 h-3 inline mr-1" />
                                Manually verified
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!editingEssays && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditEssays();
                            }}
                            className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1"
                            title="Edit essay prompts"
                          >
                            <Edit3 className="w-3 h-3" />
                            Edit Essays
                          </button>
                        )}
                        {expandedSections.essayPrompts ? (
                          <ChevronDown className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        ) : (
                          <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                        )}
                      </div>
                    </button>

                    {expandedSections.essayPrompts && (
                      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 border-t-2 border-indigo-500">
                        {editingEssays ? (
                          // EDITING MODE
                          <>
                            {editedEssays.map((essay, idx) => (
                              <div key={idx} className="bg-indigo-50/25 rounded-lg p-5 border border-indigo-200">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <h4 className="font-bold text-slate-900 mb-2">
                                      Essay {idx + 1}
                                      {essay.choice_type && (
                                        <span className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">
                                          {essay.choice_type === 'pick_one' ? 'Choice Group (Pick One)' : 'Choice Group (Optional)'}
                                        </span>
                                      )}
                                    </h4>

                                    {/* Choice Group Notice */}
                                    {essay.choice_type && (
                                      <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                                        <p className="font-medium mb-1">📋 This is a choice group prompt</p>
                                        <p>Students will see multiple options and can select which one to develop. Go to <strong>Essay Slots Library</strong> to manage prompt choices.</p>
                                      </div>
                                    )}

                                    <div className="space-y-3">
                                      <div className="flex items-center gap-4">
                                        <div className="flex-1">
                                          <label className="text-xs text-stone-600 font-medium">Title (Optional)</label>
                                          <input
                                            type="text"
                                            value={essay.friendly_title}
                                            onChange={(e) => handleUpdateEssay(idx, 'friendly_title', e.target.value)}
                                            className="w-full mt-1 p-2 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder='e.g., "Why Yale?", "Community Service Essay"'
                                          />
                                        </div>
                                        <div className="flex items-center gap-2 pt-5">
                                          <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={essay.is_optional}
                                              onChange={(e) => handleUpdateEssay(idx, 'is_optional', e.target.checked)}
                                              className="w-4 h-4 text-indigo-600 border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                            <span className="text-xs text-stone-700 font-medium">Optional</span>
                                          </label>
                                        </div>
                                      </div>
                                      <div>
                                        <label className="text-xs text-stone-600 font-medium">Prompt</label>
                                        <textarea
                                          value={essay.prompt_text}
                                          onChange={(e) => handleUpdateEssay(idx, 'prompt_text', e.target.value)}
                                          className="w-full mt-1 p-2 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                          rows={3}
                                          placeholder="Enter essay prompt..."
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-stone-600 font-medium">Word Limit</label>
                                        <input
                                          type="number"
                                          value={essay.word_limit}
                                          onChange={(e) => handleUpdateEssay(idx, 'word_limit', parseInt(e.target.value))}
                                          className="w-32 mt-1 p-2 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                          placeholder="650"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteEssay(idx)}
                                    className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded"
                                    title="Delete essay"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            <div className="flex gap-2">
                              <button
                                onClick={handleAddEssay}
                                className="px-4 py-2 text-sm bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 flex items-center gap-1"
                              >
                                <Plus className="w-4 h-4" />
                                Add Essay
                              </button>
                              <div className="flex-1" />
                              <button
                                onClick={handleSaveEssays}
                                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                              >
                                Save Changes
                              </button>
                              <button
                                onClick={handleCancelEssayEditing}
                                className="px-4 py-2 text-sm bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </>
                        ) : (
                          // DISPLAY MODE
                          <>
                            {(essaySlots.length > 0 ? essaySlots : mergedCollegeData.essay_requirements?.essays || []).map((essay, idx) => {
                              const isSlot = essaySlots.length > 0;
                              const displayTitle = isSlot && essay.friendly_title
                                ? essay.friendly_title
                                : `Essay ${idx + 1}: ${isSlot ? (essay.slot_type?.replace('_', ' ') || 'Supplemental') : (essay.type?.replace('_', ' ') || 'Supplemental Essay')}`;
                              return (
                                <div key={idx} className="bg-indigo-50/25 rounded-lg p-5 border border-indigo-200">
                                  <div className="flex items-start justify-between mb-3">
                                    <div>
                                      <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
                                        {displayTitle}
                                        {isSlot && essay.manually_verified && (
                                          <CheckCircle className="w-4 h-4 text-green-600" title="Manually verified" />
                                        )}
                                        {isSlot && essay.is_optional && (
                                          <span className="px-2 py-0.5 bg-stone-200 text-stone-700 text-xs font-medium rounded">
                                            Optional
                                          </span>
                                        )}
                                      </h4>
                                      <p className="text-sm text-stone-600">
                                        {isSlot ? essay.word_limit : (essay.wordLimit || '650')} words maximum
                                      </p>
                                    </div>
                                    <span className="px-2 py-1 bg-indigo-500 text-white text-xs font-bold rounded">
                                      {isSlot ? (essay.status?.toUpperCase() || 'EMPTY') : 'NOT STARTED'}
                                    </span>
                                  </div>
                                  <div className="bg-white rounded p-3 border border-indigo-200 mb-3">
                                    <p className="text-sm text-slate-900 italic">
                                      {isSlot ? (essay.prompt_text || 'Essay prompt not yet added') : (essay.prompt || 'Essay prompt not yet added')}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => {
                                      // Navigate to essays page with slot ID to auto-select this essay
                                      if (isSlot && essay.id) {
                                        navigate(`/college/essays?mode=essay-slots&slot=${essay.id}`);
                                      } else {
                                        // Fallback to general essays page
                                        navigate('/college/essays');
                                      }
                                    }}
                                    className="w-full py-2 bg-indigo-800 text-white font-medium rounded-lg hover:bg-indigo-900 transition-all text-sm"
                                  >
                                    Start Essay
                                  </button>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

            {/* Test Score Policy */}
            <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
              <div className="bg-indigo-50/25 border-b-2 border-indigo-500 px-6 py-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Award className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                  <h2 className="text-sm sm:text-md font-bold text-slate-900">Standardized Testing Policy</h2>
                </div>
              </div>

              <div className="p-6">
                {globalCollegeData?.test_optional || globalCollegeData?.test_blind ? (
                  <div className="bg-indigo-50/25 border-l-4 border-indigo-600 rounded-r-lg p-4 mb-4">
                    <h3 className="font-bold text-indigo-900 mb-2">
                      {globalCollegeData.test_blind ? 'Test Blind' : 'Test Optional'}
                    </h3>
                    <p className="text-sm text-indigo-800 leading-relaxed">
                      {globalCollegeData.test_blind
                        ? 'This school does not consider test scores in admissions decisions.'
                        : 'You may choose to submit SAT or ACT scores, but they are not required for admission consideration.'}
                    </p>
                  </div>
                ) : (
                  <div className="bg-indigo-50/25 border-l-4 border-indigo-600 rounded-r-lg p-4 mb-4">
                    <h3 className="font-bold text-indigo-900 mb-2">Testing Required</h3>
                    <p className="text-sm text-indigo-800 leading-relaxed">
                      This school requires standardized test scores (SAT or ACT) for admission.
                    </p>
                  </div>
                )}

                {(globalCollegeData?.sat_range || globalCollegeData?.act_range) && (
                  <div className="bg-indigo-50/25 rounded-lg p-4 mt-4">
                    <h4 className="font-bold text-slate-900 mb-3 text-sm">Test Score Ranges</h4>
                    <div className="space-y-2 text-sm">
                      {globalCollegeData.sat_range && (
                        <div className="flex justify-between">
                          <span className="text-indigo-700">SAT Middle 50%:</span>
                          <span className="font-bold text-slate-900">{globalCollegeData.sat_range}</span>
                        </div>
                      )}
                      {globalCollegeData.act_range && (
                        <div className="flex justify-between">
                          <span className="text-indigo-700">ACT Middle 50%:</span>
                          <span className="font-bold text-slate-900">{globalCollegeData.act_range}</span>
                        </div>
                      )}
                      {globalCollegeData.super_scoring && (
                        <div className="mt-3 p-2 bg-indigo-50/25 rounded">
                          <p className="text-xs text-slate-900">
                            ✓ This school superscores standardized tests
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar - Deadlines & Resources */}
          <div className="space-y-6">

                {/* Deadlines */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                    <h3 className="font-bold text-slate-900 text-sm">Key Deadlines</h3>
                  </div>
                  <div className="p-4 space-y-3 text-sm">
                    {globalCollegeData?.ed_deadline && (
                      <div className="pt-3 border-t border-stone-200 first:pt-0 first:border-t-0">
                        <div className="font-bold text-slate-900 mb-1">Early Decision (ED)</div>
                        <p className="text-xs text-stone-700">
                          {parseLocalDate(globalCollegeData.ed_deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                        </p>
                        <p className="text-xs text-stone-600">Binding commitment. Decision by mid-December.</p>
                      </div>
                    )}
                    {globalCollegeData?.ea_deadline && (
                      <div className="pt-3 border-t border-stone-200">
                        <div className="font-bold text-slate-900 mb-1">Early Action (EA)</div>
                        <p className="text-xs text-stone-700">
                          {parseLocalDate(globalCollegeData.ea_deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                        </p>
                        <p className="text-xs text-stone-600">Non-binding. Decision by mid-December.</p>
                      </div>
                    )}
                    {globalCollegeData?.rd_deadline && (
                      <div className="pt-3 border-t border-stone-200">
                        <div className="font-bold text-slate-900 mb-1">Regular Decision (RD)</div>
                        <p className="text-xs text-stone-700">
                          {parseLocalDate(globalCollegeData.rd_deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                        </p>
                        <p className="text-xs text-stone-600">Non-binding. Decision by late March.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Resources */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                    <h3 className="font-bold text-slate-900 text-sm">Application Resources</h3>
                  </div>
                  <div className="p-3 space-y-1">
                    {[
                      `${mergedCollegeData.name} Admissions Portal`,
                      'Common Application',
                      'Essay Prompts & Guidelines',
                      'First-Year Application Checklist',
                      'Application Fee Waiver Info',
                      'Contact Admissions Office'
                    ].map((link, idx) => (
                      <a
                        key={idx}
                        href="#"
                        className="flex items-center justify-between p-2 rounded hover:bg-stone-50 transition-all group"
                      >
                        <span className="text-sm text-stone-700 group-hover:text-teal-700">{link}</span>
                        <ExternalLink className="w-3 h-3 text-stone-400 group-hover:text-teal-600" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SCHOOL RESEARCH TAB */}
        {activeTab === 'research' && (
          <div className="space-y-8">

            {/* Admission Statistics Header */}
            <div className="bg-white text-slate-900 rounded-lg border border-slate-900 overflow-hidden">
              <div className="px-6 bg-violet-50/50 py-4 border-b-2 border-violet-800">
                <h2 className="text-sm sm:text-md font-bold">Admission Statistics</h2>
                <p className="text-sm text-slate-800 mt-1">Key admission data and acceptance rates</p>
              </div>
              <div className="grid grid-cols-6 gap-4 p-6">
                {globalCollegeData?.acceptance_rate && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-violet-900 mb-1">
                      {(globalCollegeData.acceptance_rate * 100).toFixed(1)}%
                    </div>
                    <div className="text-xs text-stone-400 uppercase tracking-wide">Acceptance Rate</div>
                  </div>
                )}
                {globalCollegeData?.ed_acceptance_rate && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-violet-900 mb-1">
                      {(globalCollegeData.ed_acceptance_rate * 100).toFixed(1)}%
                    </div>
                    <div className="text-xs text-stone-400 uppercase tracking-wide">Early Decision</div>
                  </div>
                )}
                {(globalCollegeData?.sat_range_25th && globalCollegeData?.sat_range_75th) && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-violet-900 mb-1">{globalCollegeData.sat_range_25th}-{globalCollegeData.sat_range_75th}</div>
                    <div className="text-xs text-stone-400 uppercase tracking-wide">SAT Range</div>
                  </div>
                )}
                {(globalCollegeData?.act_range_25th && globalCollegeData?.act_range_75th) && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-violet-900 mb-1">{globalCollegeData.act_range_25th}-{globalCollegeData.act_range_75th}</div>
                    <div className="text-xs text-stone-400 uppercase tracking-wide">ACT Range</div>
                  </div>
                )}
                {globalCollegeData?.avg_gpa_weighted && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-white mb-1">{globalCollegeData.avg_gpa_weighted}</div>
                    <div className="text-xs text-stone-400 uppercase tracking-wide">Avg GPA</div>
                  </div>
                )}
                {globalCollegeData?.yield_rate && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-violet-900 mb-1">
                      {(globalCollegeData.yield_rate * 100).toFixed(0)}%
                    </div>
                    <div className="text-xs text-stone-400 uppercase tracking-wide">Yield Rate</div>
                  </div>
                )}
              </div>
            </div>

            {/* Value Proposition - Expandable */}
            <div id="section-valueProposition" className="bg-white rounded-lg border border-slate-900 overflow-hidden">
              <button
                onClick={() => toggleSection('valueProposition')}
                className="w-full bg-violet-200 px-6 py-4 flex items-center justify-between hover:bg-violet-50 transition-all border-b-2 border-violet-500"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <Star className="w-4 sm:w-5 h-4 sm:h-5 text-slate-900" />
                  <div className="text-left">
                    <h2 className="text-sm sm:text-md font-bold text-indigo-900">Your Value Proposition</h2>
                    <p className="text-sm text-indigo-800">Build your narrative for essays and applications</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="px-2 py-1 bg-violet-800 text-white text-xs font-bold rounded">AI POWERED</span>
                  {expandedSections.valueProposition ? (
                    <ChevronDown className="w-4 sm:w-5 h-4 sm:h-5 text-indigo-900" />
                  ) : (
                    <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 text-indigo-900" />
                  )}
                </div>
              </button>

              {expandedSections.valueProposition && (
                <div className="grid grid-cols-5 border-t-2 border-slate-900">
                  {/* Left: Student Input */}
                  <div className="col-span-2 p-6 bg-stone-50 border-r-2 border-stone-200">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          What genuinely excites you about this school?
                        </label>
                        <textarea
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px] focus:border-violet-100 focus:outline-none"
                          placeholder="Click to add what genuinely excites you..."
                          value={valueProposition.excitement}
                          onChange={(e) => handleValuePropChange('excitement', e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          How would this school help you grow?
                        </label>
                        <textarea
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px] focus:border-violet-100 focus:outline-none"
                          placeholder="Think about how this school advances your goals..."
                          value={valueProposition.growth}
                          onChange={(e) => handleValuePropChange('growth', e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          What makes you different?
                        </label>
                        <textarea
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px] focus:border-violet-500 focus:outline-none"
                          placeholder="What makes you unique..."
                          value={valueProposition.unique}
                          onChange={(e) => handleValuePropChange('unique', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right: AI Suggestions */}
                  <div className="col-span-3 p-6 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-slate-900">Smart Suggestions</h3>
                      <button
                        onClick={handleGenerateValueProposition}
                        disabled={generatingValueProp}
                        className="flex items-center gap-2 px-3 py-1.5 bg-violet-700 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <RefreshCw className={`w-3 h-3 ${generatingValueProp ? 'animate-spin' : ''}`} />
                        {generatingValueProp ? 'Generating...' : 'Generate'}
                      </button>
                    </div>

                    <div className="space-y-3">
                      {mergedCollegeData.ai_suggestions?.value_proposition ? (
                        <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                          <div className="text-sm text-slate-900 leading-relaxed mb-3 prose prose-sm max-w-none prose-headings:text-slate-900 prose-p:text-slate-700 prose-strong:text-slate-900 prose-ul:text-slate-700 prose-li:text-slate-700">
                            <ReactMarkdown>{mergedCollegeData.ai_suggestions.value_proposition}</ReactMarkdown>
                          </div>
                          <button className="text-xs text-indigo-700 font-medium hover:text-indigo-800 flex items-center gap-1">
                            <Plus className="w-3 h-3" />
                            Click to use this idea
                          </button>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Sparkles className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                          <p className="text-stone-600 text-sm mb-4">Fill in the fields on the left to get AI-powered suggestions</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Strategic Research - Expandable */}
            <div id="section-strategicResearch" className="bg-white rounded-lg border border-slate-900 overflow-hidden">
              <button
                onClick={() => toggleSection('strategicResearch')}
                className="w-full bg-violet-200 px-6 py-4 flex items-center justify-between hover:bg-violet-50 transition-all border-b-2 border-violet-500"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <Target className="w-4 sm:w-5 h-4 sm:h-5 text-indigo-900" />
                  <div className="text-left">
                    <h2 className="text-sm sm:text-md font-bold text-indigo-900">Strategic Research</h2>
                    <p className="text-sm text-indigo-800">Deep dive into programs, professors, and opportunities</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="px-2 py-1 bg-violet-800 text-white text-xs font-bold rounded">AI POWERED</span>
                  {expandedSections.strategicResearch ? (
                    <ChevronDown className="w-4 sm:w-5 h-4 sm:h-5 text-indigo-900" />
                  ) : (
                    <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 text-indigo-900" />
                  )}
                </div>
              </button>

              {expandedSections.strategicResearch && (
                <div className="grid grid-cols-5 border-t-2 border-slate-900">
                  <div className="col-span-2 p-6 bg-stone-50 border-r-2 border-stone-200">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          Specific programs & professors
                        </label>
                        <textarea
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px] focus:border-violet-600 focus:outline-none"
                          placeholder="Programs, faculty, and research opportunities..."
                          value={strategicResearch.programs}
                          onChange={(e) => handleStrategicResearchChange('programs', e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          Campus life & organizations
                        </label>
                        <textarea
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px] focus:border-violet-600 focus:outline-none"
                          placeholder="How you'll engage with campus..."
                          value={strategicResearch.campus}
                          onChange={(e) => handleStrategicResearchChange('campus', e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          Location & culture notes
                        </label>
                        <textarea
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px] focus:border-violet-600 focus:outline-none"
                          placeholder="Location, culture, campus vibe..."
                          value={strategicResearch.culture}
                          onChange={(e) => handleStrategicResearchChange('culture', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-span-3 p-6 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-slate-900">Smart Suggestions</h3>
                      <button
                        onClick={handleGenerateStrategicResearch}
                        disabled={generatingResearch}
                        className="flex items-center gap-2 px-3 py-1.5 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <RefreshCw className={`w-3 h-3 ${generatingResearch ? 'animate-spin' : ''}`} />
                        {generatingResearch ? 'Generating...' : 'Generate'}
                      </button>
                    </div>

                    <div className="space-y-3">
                      {mergedCollegeData.ai_suggestions?.strategic_research ? (
                        <div className="bg-violet-50 border border-violet-300 rounded-lg p-4">
                          <div className="text-sm text-slate-900 leading-relaxed mb-3 prose prose-sm max-w-none prose-headings:text-slate-900 prose-p:text-slate-700 prose-strong:text-slate-900 prose-ul:text-slate-700 prose-li:text-slate-700">
                            <ReactMarkdown>{mergedCollegeData.ai_suggestions.strategic_research}</ReactMarkdown>
                          </div>
                            <button className="text-xs text-violet-700 font-medium hover:text-violet-800 flex items-center gap-1">
                            <Plus className="w-3 h-3" />
                            Click to use this idea
                          </button>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Sparkles className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                          <p className="text-stone-600 text-sm mb-4">Fill in the fields on the left to get AI-powered suggestions</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="col-span-2 space-y-6">

                {/* Academic Programs */}
                {(globalCollegeData?.special_programs || globalCollegeData?.student_faculty_ratio) && (
                  <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                    <div className="bg-stone-100 border-b-2 border-slate-900 px-6 py-4">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <GraduationCap className="w-4 sm:w-5 h-4 sm:h-5 text-stone-700" />
                        <h3 className="font-bold text-slate-900">Academic Programs</h3>
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      {globalCollegeData.student_faculty_ratio && (
                        <div>
                          <p className="text-sm text-stone-700">
                            <strong>Student-Faculty Ratio:</strong> {globalCollegeData.student_faculty_ratio}
                          </p>
                        </div>
                      )}
                      {globalCollegeData.special_programs && (
                        <div>
                          <p className="text-sm font-bold text-slate-900 mb-2">Special Programs:</p>
                          <p className="text-sm text-stone-700">{globalCollegeData.special_programs}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Campus Culture */}
                {globalCollegeData?.campus_culture && (
                  <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                    <div className="bg-stone-100 border-b-2 border-slate-900 px-6 py-4">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Building className="w-4 sm:w-5 h-4 sm:h-5 text-stone-700" />
                        <h3 className="font-bold text-slate-900">Campus Culture & Values</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      {(() => {
                        let campusCulture = globalCollegeData.campus_culture;

                        try {
                          // Try to parse if it's a JSON string
                          campusCulture = typeof campusCulture === 'string'
                            ? JSON.parse(campusCulture)
                            : campusCulture;
                        } catch (e) {
                          // If parsing fails, treat as plain text
                          return (
                            <p className="text-sm text-stone-700 leading-relaxed">
                              {globalCollegeData.campus_culture}
                            </p>
                          );
                        }

                        // If it's an array, render as bullet list
                        if (Array.isArray(campusCulture)) {
                          return (
                            <ul className="space-y-2">
                              {campusCulture.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2 sm:gap-3">
                                  <span className="w-1.5 h-1.5 rounded-full bg-violet-600 mt-2 flex-shrink-0"></span>
                                  <span className="text-sm text-stone-700 leading-relaxed">{item}</span>
                                </li>
                              ))}
                            </ul>
                          );
                        }

                        // Otherwise render as text
                        return (
                          <p className="text-sm text-stone-700 leading-relaxed">
                            {campusCulture}
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Sidebar - Stats */}
              <div className="space-y-6">

                {/* Campus Life */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                    <h4 className="font-bold text-slate-900 text-sm">Campus Life</h4>
                  </div>
                  <div className="p-4 space-y-2 text-sm">
                    {globalCollegeData?.student_enrollment && (
                      <div className="flex justify-between">
                        <span className="text-stone-600">Total Students</span>
                        <span className="font-bold text-slate-900">
                          {globalCollegeData.student_enrollment.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {globalCollegeData?.campus_setting && (
                      <div className="flex justify-between">
                        <span className="text-stone-600">Setting</span>
                        <span className="font-bold text-slate-900">{globalCollegeData.campus_setting}</span>
                      </div>
                    )}
                    {globalCollegeData?.location && (
                      <div className="flex justify-between">
                        <span className="text-stone-600">Location</span>
                        <span className="font-bold text-slate-900">{globalCollegeData.location}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Overview */}
                {(globalCollegeData?.tuition || globalCollegeData?.room_board) && (
                  <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                    <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                      <h4 className="font-bold text-slate-900 text-sm">Financial Overview</h4>
                    </div>
                    <div className="p-4 space-y-2 text-sm">
                      {globalCollegeData.tuition && (
                        <div className="flex justify-between">
                          <span className="text-stone-600">Tuition & Fees</span>
                          <span className="font-bold text-slate-900">
                            ${globalCollegeData.tuition.toLocaleString()}
                          </span>
                        </div>
                      )}
                      {globalCollegeData.room_board && (
                        <div className="flex justify-between">
                          <span className="text-stone-600">Room & Board</span>
                          <span className="font-bold text-slate-900">
                            ${globalCollegeData.room_board.toLocaleString()}
                          </span>
                        </div>
                      )}
                      {globalCollegeData.total_cost && (
                        <div className="flex justify-between pt-2 border-t border-stone-200">
                          <span className="text-stone-600">Total Cost</span>
                          <span className="font-bold text-slate-900">
                            ${globalCollegeData.total_cost.toLocaleString()}
                          </span>
                        </div>
                      )}
                      {globalCollegeData.avg_need_aid && (
                        <div className="flex justify-between">
                          <span className="text-stone-600">Avg. Aid</span>
                          <span className="font-bold text-violet-700">
                            ${globalCollegeData.avg_need_aid.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Outcome Metrics */}
                {(globalCollegeData?.grad_rate_4yr || globalCollegeData?.employment_rate) && (
                  <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                    <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                      <h4 className="font-bold text-slate-900 text-sm">Outcome Metrics</h4>
                    </div>
                    <div className="p-4 space-y-2 text-sm">
                      {globalCollegeData.grad_rate_4yr && (
                        <div className="flex justify-between">
                          <span className="text-stone-600">4-Year Grad</span>
                          <span className="font-bold text-slate-900">
                            {(globalCollegeData.grad_rate_4yr * 100).toFixed(0)}%
                          </span>
                        </div>
                      )}
                      {globalCollegeData.grad_rate_6yr && (
                        <div className="flex justify-between">
                          <span className="text-stone-600">6-Year Grad</span>
                          <span className="font-bold text-slate-900">
                            {(globalCollegeData.grad_rate_6yr * 100).toFixed(0)}%
                          </span>
                        </div>
                      )}
                      {globalCollegeData.avg_salary && (
                        <div className="flex justify-between">
                          <span className="text-stone-600">Avg Salary</span>
                          <span className="font-bold text-slate-900">
                            ${(globalCollegeData.avg_salary / 1000).toFixed(0)}k
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollegeStrategyV2;
